/* 3m-shipping-costs-by-state.js
 * US map widget showing 3M Thinsulate shipping costs per state.
 * Uses the Symmetry theme's <modal-dialog> custom element from main.js
 * (no Bootstrap dependency).
 */
(function () {
  function init() {
    // Idempotency guard. Shopify theme-editor section reloads re-inject this
    // <script> tag, which would otherwise run init() again on the same page
    // and append a second SVG to #map. We mark the element on first init.
    if (typeof d3 === "undefined" || typeof topojson === "undefined") return;
    const __mapEl = document.getElementById("map");
    if (!__mapEl || __mapEl.dataset.thinsulateMapInit === "true") return;
    __mapEl.dataset.thinsulateMapInit = "true";

        const width = 975;
        const height = 610;
        const BASE_COST_PER_FT = 6;

        const stateNames = {
            "01": "Alabama", "02": "Alaska", "04": "Arizona", "05": "Arkansas", "06": "California",
            "08": "Colorado", "09": "Connecticut", "10": "Delaware", "11": "District of Columbia",
            "12": "Florida", "13": "Georgia", "15": "Hawaii", "16": "Idaho", "17": "Illinois",
            "18": "Indiana", "19": "Iowa", "20": "Kansas", "21": "Kentucky", "22": "Louisiana",
            "23": "Maine", "24": "Maryland", "25": "Massachusetts", "26": "Michigan", "27": "Minnesota",
            "28": "Mississippi", "29": "Missouri", "30": "Montana", "31": "Nebraska", "32": "Nevada",
            "33": "New Hampshire", "34": "New Jersey", "35": "New Mexico", "36": "New York",
            "37": "North Carolina", "38": "North Dakota", "39": "Ohio", "40": "Oklahoma", "41": "Oregon",
            "42": "Pennsylvania", "44": "Rhode Island", "45": "South Carolina", "46": "South Dakota",
            "47": "Tennessee", "48": "Texas", "49": "Utah", "50": "Vermont", "51": "Virginia",
            "53": "Washington", "54": "West Virginia", "55": "Wisconsin", "56": "Wyoming"
        };

        const stateAbbrByFips = {
            "01": "AL", "02": "AK", "04": "AZ", "05": "AR", "06": "CA", "08": "CO", "09": "CT",
            "10": "DE", "11": "DC", "12": "FL", "13": "GA", "15": "HI", "16": "ID", "17": "IL",
            "18": "IN", "19": "IA", "20": "KS", "21": "KY", "22": "LA", "23": "ME", "24": "MD",
            "25": "MA", "26": "MI", "27": "MN", "28": "MS", "29": "MO", "30": "MT", "31": "NE",
            "32": "NV", "33": "NH", "34": "NJ", "35": "NM", "36": "NY", "37": "NC", "38": "ND",
            "39": "OH", "40": "OK", "41": "OR", "42": "PA", "44": "RI", "45": "SC", "46": "SD",
            "47": "TN", "48": "TX", "49": "UT", "50": "VT", "51": "VA", "53": "WA", "54": "WV",
            "55": "WI", "56": "WY"
        };

        const svg = d3.select("#map").append("svg").attr("viewBox", [0, 0, width, height]);
        const projection = d3.geoAlbersUsa();
        const path = d3.geoPath(projection);

        const modalEl = document.getElementById("thinsulate-state-modal");
        const modalTitle = document.getElementById("thinsulate-state-modal-label");
        const modalBody = document.getElementById("thinsulate-state-modal-body");
        /* Theme provides <modal-dialog> via main.js; nothing to construct here. */

        function formatCurrency(value) {
            return new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        }

        function formatPerFt(value) {
            return `$${Number(value).toFixed(2)}`;
        }

        function parseFtSize(ftKey) {
            if (typeof ftKey !== "string") return Number(ftKey) || 0;
            const parts = ftKey.match(/\d+/g);
            if (!parts) return 0;
            return parts.map(Number).reduce((sum, value) => sum + value, 0);
        }

        function sortFtKeys(entries) {
            return Object.keys(entries).sort((a, b) => {
                const sizeDiff = parseFtSize(a) - parseFtSize(b);
                return sizeDiff !== 0 ? sizeDiff : a.localeCompare(b);
            });
        }

        function groupEntriesForDisplay(stateCode, stateData) {
            if (!stateData) return [];

            if (stateData.locations && typeof stateData.locations === "object") {
                return Object.values(stateData.locations).map(locationData => ({
                    city: locationData.city || locationData.name || stateCode,
                    entries: locationData.entries || {}
                })).filter(group => Object.keys(group.entries).length);
            }

            if (stateData.cities) {
                if (Array.isArray(stateData.cities)) {
                    return stateData.cities.map(cityGroup => ({
                        city: cityGroup.city || cityGroup.name || stateCode,
                        entries: cityGroup.entries || {}
                    })).filter(group => Object.keys(group.entries).length);
                }

                if (typeof stateData.cities === "object") {
                    return Object.entries(stateData.cities).map(([cityName, cityData]) => ({
                        city: cityData.city || cityName,
                        entries: cityData.entries || cityData
                    })).filter(group => Object.keys(group.entries).length);
                }
            }

            if (Array.isArray(stateData.entries)) {
                const grouped = new Map();
                stateData.entries.forEach(item => {
                    const city = item.city || stateCode;
                    const ftKey = item.ft || item.size;
                    if (!ftKey) return;
                    if (!grouped.has(city)) grouped.set(city, {});
                    grouped.get(city)[ftKey] = item;
                });
                return Array.from(grouped.entries()).map(([city, entries]) => ({ city, entries }));
            }

            if (stateData.entries && typeof stateData.entries === "object") {
                const firstEntry = Object.values(stateData.entries)[0] || {};
                return [{ city: firstEntry.city || stateCode, entries: stateData.entries }];
            }

            return [];
        }

        function buildTableHtml(entries, showCityName, cityName) {
            const rowsHtml = sortFtKeys(entries).map(ftKey => {
                const ft = parseFtSize(ftKey);
                const shippingCost = Number(entries[ftKey].total_charge || 0);
                const overallCost = (BASE_COST_PER_FT * ft) + shippingCost;

                return `
                    <tr>
                        <td class="fw-semibold">${ftKey}</td>
                        <td>${formatCurrency(shippingCost)}</td>
                        <td class="fw-semibold">${formatCurrency(overallCost)}</td>
                    </tr>
                `;
            }).join("");

            return `
                <section class="table-card">
                    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                        ${showCityName ? `<div class="city-heading mb-0">${cityName}</div>` : `<div class="city-heading mb-0">Shipping Rates</div>`}
                    </div>
                    <div class="modern-table-wrap">
                        <div class="table-responsive">
                            <table class="modern-table">
                                <thead>
                                    <tr>
                                        <th>Size</th>
                                        <th>Shipping cost</th>
                                        <th>Overall cost</th>
                                    </tr>
                                </thead>
                                <tbody>${rowsHtml}</tbody>
                            </table>
                        </div>
                    </div>
                </section>
            `;
        }

        function renderStateModal(stateCode, stateName, shippingData) {
            const stateData = shippingData[stateCode];
            modalTitle.textContent = stateName;

            if (!stateData) {
                modalBody.innerHTML = `<div class="alert alert-warning mb-0">No shipping data found for ${stateName}.</div>`;
                modalEl.open();
                return;
            }

            const groups = groupEntriesForDisplay(stateCode, stateData);
            const showCityName = groups.length > 1;

            if (!groups.length) {
                modalBody.innerHTML = `<div class="alert alert-warning mb-0">No shipping entries found for ${stateName}.</div>`;
            } else {
                let modalHtml = "";
                modalHtml += groups.map(group => buildTableHtml(group.entries, showCityName, group.city)).join("");
                modalBody.innerHTML = modalHtml;
            }

            modalEl.open();
        }

        const shippingData = {
  "AK": {
    "locations": {
      "Palmer|99645": {
        "city": "Palmer",
        "zip": "99645",
        "entries": {
          "5ft": {
            "zip": "99645",
            "city": "Palmer",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "99645",
            "city": "Palmer",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "99645",
            "city": "Palmer",
            "total_charge": 132,
            "price_per_ft": 8.74
          },
          "20ft": {
            "zip": "99645",
            "city": "Palmer",
            "total_charge": 131,
            "price_per_ft": 6.55
          },
          "25ft": {
            "zip": "99645",
            "city": "Palmer",
            "total_charge": 131,
            "price_per_ft": 5.24
          },
          "30ft": {
            "zip": "99645",
            "city": "Palmer",
            "total_charge": 146,
            "price_per_ft": 4.84
          },
          "35ft": {
            "zip": "99645",
            "city": "Palmer",
            "total_charge": 146,
            "price_per_ft": 4.16
          },
          "40ft": {
            "zip": "99645",
            "city": "Palmer",
            "total_charge": 160,
            "price_per_ft": 4.0
          },
          "45ft": {
            "zip": "99645",
            "city": "Palmer",
            "total_charge": 161,
            "price_per_ft": 3.56
          },
          "50ft": {
            "zip": "99645",
            "city": "Palmer",
            "total_charge": 160,
            "price_per_ft": 3.2
          },
          "55ft": {
            "zip": "99645",
            "city": "Palmer",
            "total_charge": 175,
            "price_per_ft": 3.17
          },
          "60ft": {
            "zip": "99645",
            "city": "Palmer",
            "total_charge": 393,
            "price_per_ft": 6.55
          }
        },
        "summary": {
          "avg_total_charge": 148.67,
          "avg_price_per_ft": 4.72,
          "min_total_charge": 17.0,
          "max_total_charge": 393.0
        }
      }
    }
  },
  "AL": {
    "locations": {
      "Gulf Shores|36542": {
        "city": "Gulf Shores",
        "zip": "36542",
        "entries": {
          "5ft": {
            "zip": "36542",
            "city": "Gulf Shores",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "36542",
            "city": "Gulf Shores",
            "total_charge": 41,
            "price_per_ft": 4.1
          },
          "15ft": {
            "zip": "36542",
            "city": "Gulf Shores",
            "total_charge": 71,
            "price_per_ft": 4.67
          },
          "20ft": {
            "zip": "36542",
            "city": "Gulf Shores",
            "total_charge": 70,
            "price_per_ft": 3.5
          },
          "25ft": {
            "zip": "36542",
            "city": "Gulf Shores",
            "total_charge": 70,
            "price_per_ft": 2.8
          },
          "30ft": {
            "zip": "36542",
            "city": "Gulf Shores",
            "total_charge": 77,
            "price_per_ft": 2.54
          },
          "35ft": {
            "zip": "36542",
            "city": "Gulf Shores",
            "total_charge": 77,
            "price_per_ft": 2.19
          },
          "40ft": {
            "zip": "36542",
            "city": "Gulf Shores",
            "total_charge": 80,
            "price_per_ft": 1.98
          },
          "45ft": {
            "zip": "36542",
            "city": "Gulf Shores",
            "total_charge": 80,
            "price_per_ft": 1.76
          },
          "50ft": {
            "zip": "36542",
            "city": "Gulf Shores",
            "total_charge": 79,
            "price_per_ft": 1.58
          },
          "55ft": {
            "zip": "36542",
            "city": "Gulf Shores",
            "total_charge": 83,
            "price_per_ft": 1.5
          },
          "60ft": {
            "zip": "36542",
            "city": "Gulf Shores",
            "total_charge": 102,
            "price_per_ft": 1.7
          }
        },
        "summary": {
          "avg_total_charge": 70.58,
          "avg_price_per_ft": 2.64,
          "min_total_charge": 17.0,
          "max_total_charge": 102.0
        }
      }
    }
  },
  "AR": {
    "locations": {
      "Gravette|72736": {
        "city": "Gravette",
        "zip": "72736",
        "entries": {
          "5ft": {
            "zip": "72736",
            "city": "Gravette",
            "total_charge": 34,
            "price_per_ft": 6.8
          },
          "10ft": {
            "zip": "72736",
            "city": "Gravette",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "72736",
            "city": "Gravette",
            "total_charge": 67,
            "price_per_ft": 4.41
          },
          "20ft": {
            "zip": "72736",
            "city": "Gravette",
            "total_charge": 66,
            "price_per_ft": 3.3
          },
          "25ft": {
            "zip": "72736",
            "city": "Gravette",
            "total_charge": 66,
            "price_per_ft": 2.64
          },
          "30ft": {
            "zip": "72736",
            "city": "Gravette",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "72736",
            "city": "Gravette",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "72736",
            "city": "Gravette",
            "total_charge": 74,
            "price_per_ft": 1.85
          },
          "45ft": {
            "zip": "72736",
            "city": "Gravette",
            "total_charge": 75,
            "price_per_ft": 1.65
          },
          "50ft": {
            "zip": "72736",
            "city": "Gravette",
            "total_charge": 74,
            "price_per_ft": 1.48
          },
          "55ft": {
            "zip": "72736",
            "city": "Gravette",
            "total_charge": 76,
            "price_per_ft": 1.37
          },
          "60ft": {
            "zip": "72736",
            "city": "Gravette",
            "total_charge": 93,
            "price_per_ft": 1.54
          }
        },
        "summary": {
          "avg_total_charge": 66.83,
          "avg_price_per_ft": 2.73,
          "min_total_charge": 32.0,
          "max_total_charge": 93.0
        }
      }
    }
  },
  "AZ": {
    "locations": {
      "Anthem|85086": {
        "city": "Anthem",
        "zip": "85086",
        "entries": {
          "5ft": {
            "zip": "85086",
            "city": "Anthem",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "85086",
            "city": "Anthem",
            "total_charge": 33,
            "price_per_ft": 3.3
          },
          "15ft": {
            "zip": "85086",
            "city": "Anthem",
            "total_charge": 66,
            "price_per_ft": 4.34
          },
          "20ft": {
            "zip": "85086",
            "city": "Anthem",
            "total_charge": 65,
            "price_per_ft": 3.25
          },
          "25ft": {
            "zip": "85086",
            "city": "Anthem",
            "total_charge": 65,
            "price_per_ft": 2.6
          },
          "30ft": {
            "zip": "85086",
            "city": "Anthem",
            "total_charge": 72,
            "price_per_ft": 2.37
          },
          "35ft": {
            "zip": "85086",
            "city": "Anthem",
            "total_charge": 66,
            "price_per_ft": 1.86
          },
          "40ft": {
            "zip": "85086",
            "city": "Anthem",
            "total_charge": 74,
            "price_per_ft": 1.83
          },
          "45ft": {
            "zip": "85086",
            "city": "Anthem",
            "total_charge": 71,
            "price_per_ft": 1.56
          },
          "50ft": {
            "zip": "85086",
            "city": "Anthem",
            "total_charge": 73,
            "price_per_ft": 1.46
          },
          "55ft": {
            "zip": "85086",
            "city": "Anthem",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "85086",
            "city": "Anthem",
            "total_charge": 81,
            "price_per_ft": 1.35
          }
        },
        "summary": {
          "avg_total_charge": 63.17,
          "avg_price_per_ft": 2.39,
          "min_total_charge": 17.0,
          "max_total_charge": 81.0
        }
      }
    }
  },
  "CA": {
    "locations": {
      "California Average|CA": {
        "city": "California Average",
        "zip": "CA",
        "entries": {
          "5ft": {
            "zip": "CA",
            "city": "California Average",
            "total_charge": 20,
            "price_per_ft": 4.0
          },
          "10ft": {
            "zip": "CA",
            "city": "California Average",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "CA",
            "city": "California Average",
            "total_charge": 50,
            "price_per_ft": 3.27
          },
          "20ft": {
            "zip": "CA",
            "city": "California Average",
            "total_charge": 50,
            "price_per_ft": 2.46
          },
          "25ft": {
            "zip": "CA",
            "city": "California Average",
            "total_charge": 49,
            "price_per_ft": 1.96
          },
          "30ft": {
            "zip": "CA",
            "city": "California Average",
            "total_charge": 53,
            "price_per_ft": 1.74
          },
          "35ft": {
            "zip": "CA",
            "city": "California Average",
            "total_charge": 53,
            "price_per_ft": 1.49
          },
          "40ft": {
            "zip": "CA",
            "city": "California Average",
            "total_charge": 54,
            "price_per_ft": 1.35
          },
          "45ft": {
            "zip": "CA",
            "city": "California Average",
            "total_charge": 61,
            "price_per_ft": 1.35
          },
          "50ft": {
            "zip": "CA",
            "city": "California Average",
            "total_charge": 68,
            "price_per_ft": 1.35
          },
          "55ft": {
            "zip": "CA",
            "city": "California Average",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "CA",
            "city": "California Average",
            "total_charge": 81,
            "price_per_ft": 1.35
          }
        },
        "summary": {
          "avg_total_charge": 53.83,
          "avg_price_per_ft": 2.07,
          "min_total_charge": 20.0,
          "max_total_charge": 81.0
        }
      }
    }
  },
  "CO": {
    "locations": {
      "Golden|80403": {
        "city": "Golden",
        "zip": "80403",
        "entries": {
          "5ft": {
            "zip": "80403",
            "city": "Golden",
            "total_charge": 29,
            "price_per_ft": 5.8
          },
          "10ft": {
            "zip": "80403",
            "city": "Golden",
            "total_charge": 33,
            "price_per_ft": 3.3
          },
          "15ft": {
            "zip": "80403",
            "city": "Golden",
            "total_charge": 54,
            "price_per_ft": 3.54
          },
          "20ft": {
            "zip": "80403",
            "city": "Golden",
            "total_charge": 53,
            "price_per_ft": 2.65
          },
          "25ft": {
            "zip": "80403",
            "city": "Golden",
            "total_charge": 53,
            "price_per_ft": 2.12
          },
          "30ft": {
            "zip": "80403",
            "city": "Golden",
            "total_charge": 57,
            "price_per_ft": 1.87
          },
          "35ft": {
            "zip": "80403",
            "city": "Golden",
            "total_charge": 56,
            "price_per_ft": 1.6
          },
          "40ft": {
            "zip": "80403",
            "city": "Golden",
            "total_charge": 58,
            "price_per_ft": 1.45
          },
          "45ft": {
            "zip": "80403",
            "city": "Golden",
            "total_charge": 61,
            "price_per_ft": 1.35
          },
          "50ft": {
            "zip": "80403",
            "city": "Golden",
            "total_charge": 68,
            "price_per_ft": 1.35
          },
          "55ft": {
            "zip": "80403",
            "city": "Golden",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "80403",
            "city": "Golden",
            "total_charge": 81,
            "price_per_ft": 1.35
          }
        },
        "summary": {
          "avg_total_charge": 56.5,
          "avg_price_per_ft": 2.31,
          "min_total_charge": 29.0,
          "max_total_charge": 81.0
        }
      }
    }
  },
  "CT": {
    "locations": {
      "Middlefield|06455": {
        "city": "Middlefield",
        "zip": "06455",
        "entries": {
          "5ft": {
            "zip": "06455",
            "city": "Middlefield",
            "total_charge": 33,
            "price_per_ft": 6.6
          },
          "10ft": {
            "zip": "06455",
            "city": "Middlefield",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "06455",
            "city": "Middlefield",
            "total_charge": 68,
            "price_per_ft": 4.47
          },
          "20ft": {
            "zip": "06455",
            "city": "Middlefield",
            "total_charge": 67,
            "price_per_ft": 3.35
          },
          "25ft": {
            "zip": "06455",
            "city": "Middlefield",
            "total_charge": 67,
            "price_per_ft": 2.68
          },
          "30ft": {
            "zip": "06455",
            "city": "Middlefield",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "06455",
            "city": "Middlefield",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "06455",
            "city": "Middlefield",
            "total_charge": 76,
            "price_per_ft": 1.88
          },
          "45ft": {
            "zip": "06455",
            "city": "Middlefield",
            "total_charge": 76,
            "price_per_ft": 1.67
          },
          "50ft": {
            "zip": "06455",
            "city": "Middlefield",
            "total_charge": 75,
            "price_per_ft": 1.5
          },
          "55ft": {
            "zip": "06455",
            "city": "Middlefield",
            "total_charge": 79,
            "price_per_ft": 1.42
          },
          "60ft": {
            "zip": "06455",
            "city": "Middlefield",
            "total_charge": 99,
            "price_per_ft": 1.64
          }
        },
        "summary": {
          "avg_total_charge": 68.08,
          "avg_price_per_ft": 2.74,
          "min_total_charge": 32.0,
          "max_total_charge": 99.0
        }
      }
    }
  },
  "DC": {
    "locations": {
      "Washington|20002": {
        "city": "Washington",
        "zip": "20002",
        "entries": {
          "5ft": {
            "zip": "20002",
            "city": "Washington",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "20002",
            "city": "Washington",
            "total_charge": 45,
            "price_per_ft": 4.41
          },
          "15ft": {
            "zip": "20002",
            "city": "Washington",
            "total_charge": 68,
            "price_per_ft": 4.47
          },
          "20ft": {
            "zip": "20002",
            "city": "Washington",
            "total_charge": 67,
            "price_per_ft": 3.35
          },
          "25ft": {
            "zip": "20002",
            "city": "Washington",
            "total_charge": 67,
            "price_per_ft": 2.68
          },
          "30ft": {
            "zip": "20002",
            "city": "Washington",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "20002",
            "city": "Washington",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "20002",
            "city": "Washington",
            "total_charge": 76,
            "price_per_ft": 1.88
          },
          "45ft": {
            "zip": "20002",
            "city": "Washington",
            "total_charge": 76,
            "price_per_ft": 1.67
          },
          "50ft": {
            "zip": "20002",
            "city": "Washington",
            "total_charge": 75,
            "price_per_ft": 1.5
          },
          "55ft": {
            "zip": "20002",
            "city": "Washington",
            "total_charge": 79,
            "price_per_ft": 1.42
          },
          "60ft": {
            "zip": "20002",
            "city": "Washington",
            "total_charge": 99,
            "price_per_ft": 1.64
          }
        },
        "summary": {
          "avg_total_charge": 67.83,
          "avg_price_per_ft": 2.57,
          "min_total_charge": 17.0,
          "max_total_charge": 99.0
        }
      }
    }
  },
  "DE": {
    "locations": {
      "Greenville|19807": {
        "city": "Greenville",
        "zip": "19807",
        "entries": {
          "5ft": {
            "zip": "19807",
            "city": "Greenville",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "19807",
            "city": "Greenville",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "19807",
            "city": "Greenville",
            "total_charge": 71,
            "price_per_ft": 4.67
          },
          "20ft": {
            "zip": "19807",
            "city": "Greenville",
            "total_charge": 70,
            "price_per_ft": 3.5
          },
          "25ft": {
            "zip": "19807",
            "city": "Greenville",
            "total_charge": 70,
            "price_per_ft": 2.8
          },
          "30ft": {
            "zip": "19807",
            "city": "Greenville",
            "total_charge": 77,
            "price_per_ft": 2.54
          },
          "35ft": {
            "zip": "19807",
            "city": "Greenville",
            "total_charge": 77,
            "price_per_ft": 2.19
          },
          "40ft": {
            "zip": "19807",
            "city": "Greenville",
            "total_charge": 80,
            "price_per_ft": 1.98
          },
          "45ft": {
            "zip": "19807",
            "city": "Greenville",
            "total_charge": 80,
            "price_per_ft": 1.76
          },
          "50ft": {
            "zip": "19807",
            "city": "Greenville",
            "total_charge": 79,
            "price_per_ft": 1.58
          },
          "55ft": {
            "zip": "19807",
            "city": "Greenville",
            "total_charge": 83,
            "price_per_ft": 1.5
          },
          "60ft": {
            "zip": "19807",
            "city": "Greenville",
            "total_charge": 99,
            "price_per_ft": 1.64
          }
        },
        "summary": {
          "avg_total_charge": 69.58,
          "avg_price_per_ft": 2.56,
          "min_total_charge": 17.0,
          "max_total_charge": 99.0
        }
      }
    }
  },
  "FL": {
    "locations": {
      "Lithia|33547": {
        "city": "Lithia",
        "zip": "33547",
        "entries": {
          "5ft": {
            "zip": "33547",
            "city": "Lithia",
            "total_charge": 33,
            "price_per_ft": 6.6
          },
          "10ft": {
            "zip": "33547",
            "city": "Lithia",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "33547",
            "city": "Lithia",
            "total_charge": 71,
            "price_per_ft": 4.67
          },
          "20ft": {
            "zip": "33547",
            "city": "Lithia",
            "total_charge": 70,
            "price_per_ft": 3.5
          },
          "25ft": {
            "zip": "33547",
            "city": "Lithia",
            "total_charge": 70,
            "price_per_ft": 2.8
          },
          "30ft": {
            "zip": "33547",
            "city": "Lithia",
            "total_charge": 77,
            "price_per_ft": 2.54
          },
          "35ft": {
            "zip": "33547",
            "city": "Lithia",
            "total_charge": 77,
            "price_per_ft": 2.19
          },
          "40ft": {
            "zip": "33547",
            "city": "Lithia",
            "total_charge": 80,
            "price_per_ft": 1.98
          },
          "45ft": {
            "zip": "33547",
            "city": "Lithia",
            "total_charge": 80,
            "price_per_ft": 1.76
          },
          "50ft": {
            "zip": "33547",
            "city": "Lithia",
            "total_charge": 79,
            "price_per_ft": 1.58
          },
          "55ft": {
            "zip": "33547",
            "city": "Lithia",
            "total_charge": 83,
            "price_per_ft": 1.5
          },
          "60ft": {
            "zip": "33547",
            "city": "Lithia",
            "total_charge": 102,
            "price_per_ft": 1.7
          }
        },
        "summary": {
          "avg_total_charge": 71.17,
          "avg_price_per_ft": 2.83,
          "min_total_charge": 32.0,
          "max_total_charge": 102.0
        }
      }
    }
  },
  "GA": {
    "locations": {
      "Mineral Bluff|30559": {
        "city": "Mineral Bluff",
        "zip": "30559",
        "entries": {
          "5ft": {
            "zip": "30559",
            "city": "Mineral Bluff",
            "total_charge": 36,
            "price_per_ft": 7.2
          },
          "10ft": {
            "zip": "30559",
            "city": "Mineral Bluff",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "30559",
            "city": "Mineral Bluff",
            "total_charge": 86,
            "price_per_ft": 5.67
          },
          "20ft": {
            "zip": "30559",
            "city": "Mineral Bluff",
            "total_charge": 85,
            "price_per_ft": 4.25
          },
          "25ft": {
            "zip": "30559",
            "city": "Mineral Bluff",
            "total_charge": 85,
            "price_per_ft": 3.4
          },
          "30ft": {
            "zip": "30559",
            "city": "Mineral Bluff",
            "total_charge": 92,
            "price_per_ft": 3.04
          },
          "35ft": {
            "zip": "30559",
            "city": "Mineral Bluff",
            "total_charge": 91,
            "price_per_ft": 2.6
          },
          "40ft": {
            "zip": "30559",
            "city": "Mineral Bluff",
            "total_charge": 94,
            "price_per_ft": 2.35
          },
          "45ft": {
            "zip": "30559",
            "city": "Mineral Bluff",
            "total_charge": 95,
            "price_per_ft": 2.09
          },
          "50ft": {
            "zip": "30559",
            "city": "Mineral Bluff",
            "total_charge": 94,
            "price_per_ft": 1.88
          },
          "55ft": {
            "zip": "30559",
            "city": "Mineral Bluff",
            "total_charge": 98,
            "price_per_ft": 1.77
          },
          "60ft": {
            "zip": "30559",
            "city": "Mineral Bluff",
            "total_charge": 119,
            "price_per_ft": 1.97
          }
        },
        "summary": {
          "avg_total_charge": 83.92,
          "avg_price_per_ft": 3.29,
          "min_total_charge": 32.0,
          "max_total_charge": 119.0
        }
      }
    }
  },
  "HI": {
    "locations": {
      "Lihue|96766": {
        "city": "Lihue",
        "zip": "96766",
        "entries": {
          "5ft": {
            "zip": "96766",
            "city": "Lihue",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "96766",
            "city": "Lihue",
            "total_charge": 41,
            "price_per_ft": 4.1
          },
          "15ft": {
            "zip": "96766",
            "city": "Lihue",
            "total_charge": 156,
            "price_per_ft": 10.34
          },
          "20ft": {
            "zip": "96766",
            "city": "Lihue",
            "total_charge": 155,
            "price_per_ft": 7.75
          },
          "25ft": {
            "zip": "96766",
            "city": "Lihue",
            "total_charge": 155,
            "price_per_ft": 6.2
          },
          "30ft": {
            "zip": "96766",
            "city": "Lihue",
            "total_charge": 171,
            "price_per_ft": 5.67
          },
          "35ft": {
            "zip": "96766",
            "city": "Lihue",
            "total_charge": 171,
            "price_per_ft": 4.87
          },
          "40ft": {
            "zip": "96766",
            "city": "Lihue",
            "total_charge": 187,
            "price_per_ft": 4.66
          },
          "45ft": {
            "zip": "96766",
            "city": "Lihue",
            "total_charge": 187,
            "price_per_ft": 4.14
          },
          "50ft": {
            "zip": "96766",
            "city": "Lihue",
            "total_charge": 186,
            "price_per_ft": 3.72
          },
          "55ft": {
            "zip": "96766",
            "city": "Lihue",
            "total_charge": 202,
            "price_per_ft": 3.66
          },
          "60ft": {
            "zip": "96766",
            "city": "Lihue",
            "total_charge": 420,
            "price_per_ft": 7.0
          }
        },
        "summary": {
          "avg_total_charge": 170.67,
          "avg_price_per_ft": 5.46,
          "min_total_charge": 17.0,
          "max_total_charge": 420.0
        }
      }
    }
  },
  "IA": {
    "locations": {
      "Story City|50248": {
        "city": "Story City",
        "zip": "50248",
        "entries": {
          "5ft": {
            "zip": "50248",
            "city": "Story City",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "50248",
            "city": "Story City",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "50248",
            "city": "Story City",
            "total_charge": 67,
            "price_per_ft": 4.41
          },
          "20ft": {
            "zip": "50248",
            "city": "Story City",
            "total_charge": 66,
            "price_per_ft": 3.3
          },
          "25ft": {
            "zip": "50248",
            "city": "Story City",
            "total_charge": 66,
            "price_per_ft": 2.64
          },
          "30ft": {
            "zip": "50248",
            "city": "Story City",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "50248",
            "city": "Story City",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "50248",
            "city": "Story City",
            "total_charge": 74,
            "price_per_ft": 1.85
          },
          "45ft": {
            "zip": "50248",
            "city": "Story City",
            "total_charge": 75,
            "price_per_ft": 1.65
          },
          "50ft": {
            "zip": "50248",
            "city": "Story City",
            "total_charge": 74,
            "price_per_ft": 1.48
          },
          "55ft": {
            "zip": "50248",
            "city": "Story City",
            "total_charge": 76,
            "price_per_ft": 1.37
          },
          "60ft": {
            "zip": "50248",
            "city": "Story City",
            "total_charge": 93,
            "price_per_ft": 1.54
          }
        },
        "summary": {
          "avg_total_charge": 65.42,
          "avg_price_per_ft": 2.44,
          "min_total_charge": 17.0,
          "max_total_charge": 93.0
        }
      }
    }
  },
  "ID": {
    "locations": {
      "Meridian|83642": {
        "city": "Meridian",
        "zip": "83642",
        "entries": {
          "5ft": {
            "zip": "83642",
            "city": "Meridian",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "83642",
            "city": "Meridian",
            "total_charge": 30,
            "price_per_ft": 3.0
          },
          "15ft": {
            "zip": "83642",
            "city": "Meridian",
            "total_charge": 42,
            "price_per_ft": 2.8
          },
          "20ft": {
            "zip": "83642",
            "city": "Meridian",
            "total_charge": 42,
            "price_per_ft": 2.1
          },
          "25ft": {
            "zip": "83642",
            "city": "Meridian",
            "total_charge": 42,
            "price_per_ft": 1.68
          },
          "30ft": {
            "zip": "83642",
            "city": "Meridian",
            "total_charge": 45,
            "price_per_ft": 1.5
          },
          "35ft": {
            "zip": "83642",
            "city": "Meridian",
            "total_charge": 48,
            "price_per_ft": 1.35
          },
          "40ft": {
            "zip": "83642",
            "city": "Meridian",
            "total_charge": 54,
            "price_per_ft": 1.35
          },
          "45ft": {
            "zip": "83642",
            "city": "Meridian",
            "total_charge": 61,
            "price_per_ft": 1.35
          },
          "50ft": {
            "zip": "83642",
            "city": "Meridian",
            "total_charge": 68,
            "price_per_ft": 1.35
          },
          "55ft": {
            "zip": "83642",
            "city": "Meridian",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "83642",
            "city": "Meridian",
            "total_charge": 81,
            "price_per_ft": 1.35
          }
        },
        "summary": {
          "avg_total_charge": 50.42,
          "avg_price_per_ft": 1.88,
          "min_total_charge": 17.0,
          "max_total_charge": 81.0
        }
      }
    }
  },
  "IL": {
    "locations": {
      "Chicago|60618": {
        "city": "Chicago",
        "zip": "60618",
        "entries": {
          "5ft": {
            "zip": "60618",
            "city": "Chicago",
            "total_charge": 32,
            "price_per_ft": 6.4
          },
          "10ft": {
            "zip": "60618",
            "city": "Chicago",
            "total_charge": 37,
            "price_per_ft": 3.7
          },
          "15ft": {
            "zip": "60618",
            "city": "Chicago",
            "total_charge": 62,
            "price_per_ft": 4.07
          },
          "20ft": {
            "zip": "60618",
            "city": "Chicago",
            "total_charge": 61,
            "price_per_ft": 3.05
          },
          "25ft": {
            "zip": "60618",
            "city": "Chicago",
            "total_charge": 61,
            "price_per_ft": 2.44
          },
          "30ft": {
            "zip": "60618",
            "city": "Chicago",
            "total_charge": 68,
            "price_per_ft": 2.25
          },
          "35ft": {
            "zip": "60618",
            "city": "Chicago",
            "total_charge": 68,
            "price_per_ft": 1.92
          },
          "40ft": {
            "zip": "60618",
            "city": "Chicago",
            "total_charge": 70,
            "price_per_ft": 1.73
          },
          "45ft": {
            "zip": "60618",
            "city": "Chicago",
            "total_charge": 70,
            "price_per_ft": 1.54
          },
          "50ft": {
            "zip": "60618",
            "city": "Chicago",
            "total_charge": 69,
            "price_per_ft": 1.38
          },
          "55ft": {
            "zip": "60618",
            "city": "Chicago",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "60618",
            "city": "Chicago",
            "total_charge": 87,
            "price_per_ft": 1.44
          }
        },
        "summary": {
          "avg_total_charge": 63.33,
          "avg_price_per_ft": 2.61,
          "min_total_charge": 32.0,
          "max_total_charge": 87.0
        }
      }
    }
  },
  "IN": {
    "locations": {
      "Indianapolis|46250": {
        "city": "Indianapolis",
        "zip": "46250",
        "entries": {
          "5ft": {
            "zip": "46250",
            "city": "Indianapolis",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "46250",
            "city": "Indianapolis",
            "total_charge": 41,
            "price_per_ft": 4.1
          },
          "15ft": {
            "zip": "46250",
            "city": "Indianapolis",
            "total_charge": 68,
            "price_per_ft": 4.47
          },
          "20ft": {
            "zip": "46250",
            "city": "Indianapolis",
            "total_charge": 67,
            "price_per_ft": 3.35
          },
          "25ft": {
            "zip": "46250",
            "city": "Indianapolis",
            "total_charge": 67,
            "price_per_ft": 2.68
          },
          "30ft": {
            "zip": "46250",
            "city": "Indianapolis",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "46250",
            "city": "Indianapolis",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "46250",
            "city": "Indianapolis",
            "total_charge": 76,
            "price_per_ft": 1.88
          },
          "45ft": {
            "zip": "46250",
            "city": "Indianapolis",
            "total_charge": 76,
            "price_per_ft": 1.67
          },
          "50ft": {
            "zip": "46250",
            "city": "Indianapolis",
            "total_charge": 75,
            "price_per_ft": 1.5
          },
          "55ft": {
            "zip": "46250",
            "city": "Indianapolis",
            "total_charge": 79,
            "price_per_ft": 1.42
          },
          "60ft": {
            "zip": "46250",
            "city": "Indianapolis",
            "total_charge": 99,
            "price_per_ft": 1.64
          }
        },
        "summary": {
          "avg_total_charge": 67.5,
          "avg_price_per_ft": 2.55,
          "min_total_charge": 17.0,
          "max_total_charge": 99.0
        }
      }
    }
  },
  "KS": {
    "locations": {
      "Baldwin City|66006": {
        "city": "Baldwin City",
        "zip": "66006",
        "entries": {
          "5ft": {
            "zip": "66006",
            "city": "Baldwin City",
            "total_charge": 32,
            "price_per_ft": 6.4
          },
          "10ft": {
            "zip": "66006",
            "city": "Baldwin City",
            "total_charge": 37,
            "price_per_ft": 3.7
          },
          "15ft": {
            "zip": "66006",
            "city": "Baldwin City",
            "total_charge": 66,
            "price_per_ft": 4.34
          },
          "20ft": {
            "zip": "66006",
            "city": "Baldwin City",
            "total_charge": 65,
            "price_per_ft": 3.25
          },
          "25ft": {
            "zip": "66006",
            "city": "Baldwin City",
            "total_charge": 65,
            "price_per_ft": 2.6
          },
          "30ft": {
            "zip": "66006",
            "city": "Baldwin City",
            "total_charge": 72,
            "price_per_ft": 2.37
          },
          "35ft": {
            "zip": "66006",
            "city": "Baldwin City",
            "total_charge": 72,
            "price_per_ft": 2.03
          },
          "40ft": {
            "zip": "66006",
            "city": "Baldwin City",
            "total_charge": 74,
            "price_per_ft": 1.83
          },
          "45ft": {
            "zip": "66006",
            "city": "Baldwin City",
            "total_charge": 74,
            "price_per_ft": 1.63
          },
          "50ft": {
            "zip": "66006",
            "city": "Baldwin City",
            "total_charge": 73,
            "price_per_ft": 1.46
          },
          "55ft": {
            "zip": "66006",
            "city": "Baldwin City",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "66006",
            "city": "Baldwin City",
            "total_charge": 92,
            "price_per_ft": 1.52
          }
        },
        "summary": {
          "avg_total_charge": 66.42,
          "avg_price_per_ft": 2.71,
          "min_total_charge": 32.0,
          "max_total_charge": 92.0
        }
      }
    }
  },
  "KY": {
    "locations": {
      "Nicholasville|40356": {
        "city": "Nicholasville",
        "zip": "40356",
        "entries": {
          "5ft": {
            "zip": "40356",
            "city": "Nicholasville",
            "total_charge": 33,
            "price_per_ft": 6.6
          },
          "10ft": {
            "zip": "40356",
            "city": "Nicholasville",
            "total_charge": 41,
            "price_per_ft": 4.1
          },
          "15ft": {
            "zip": "40356",
            "city": "Nicholasville",
            "total_charge": 68,
            "price_per_ft": 4.47
          },
          "20ft": {
            "zip": "40356",
            "city": "Nicholasville",
            "total_charge": 67,
            "price_per_ft": 3.35
          },
          "25ft": {
            "zip": "40356",
            "city": "Nicholasville",
            "total_charge": 67,
            "price_per_ft": 2.68
          },
          "30ft": {
            "zip": "40356",
            "city": "Nicholasville",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "40356",
            "city": "Nicholasville",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "40356",
            "city": "Nicholasville",
            "total_charge": 76,
            "price_per_ft": 1.88
          },
          "45ft": {
            "zip": "40356",
            "city": "Nicholasville",
            "total_charge": 76,
            "price_per_ft": 1.67
          },
          "50ft": {
            "zip": "40356",
            "city": "Nicholasville",
            "total_charge": 75,
            "price_per_ft": 1.5
          },
          "55ft": {
            "zip": "40356",
            "city": "Nicholasville",
            "total_charge": 79,
            "price_per_ft": 1.42
          },
          "60ft": {
            "zip": "40356",
            "city": "Nicholasville",
            "total_charge": 99,
            "price_per_ft": 1.64
          }
        },
        "summary": {
          "avg_total_charge": 68.83,
          "avg_price_per_ft": 2.81,
          "min_total_charge": 33.0,
          "max_total_charge": 99.0
        }
      }
    }
  },
  "LA": {
    "locations": {
      "Bossier City|71111": {
        "city": "Bossier City",
        "zip": "71111",
        "entries": {
          "5ft": {
            "zip": "71111",
            "city": "Bossier City",
            "total_charge": 32,
            "price_per_ft": 6.4
          },
          "10ft": {
            "zip": "71111",
            "city": "Bossier City",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "71111",
            "city": "Bossier City",
            "total_charge": 62,
            "price_per_ft": 4.07
          },
          "20ft": {
            "zip": "71111",
            "city": "Bossier City",
            "total_charge": 61,
            "price_per_ft": 3.05
          },
          "25ft": {
            "zip": "71111",
            "city": "Bossier City",
            "total_charge": 61,
            "price_per_ft": 2.44
          },
          "30ft": {
            "zip": "71111",
            "city": "Bossier City",
            "total_charge": 68,
            "price_per_ft": 2.25
          },
          "35ft": {
            "zip": "71111",
            "city": "Bossier City",
            "total_charge": 68,
            "price_per_ft": 1.92
          },
          "40ft": {
            "zip": "71111",
            "city": "Bossier City",
            "total_charge": 70,
            "price_per_ft": 1.73
          },
          "45ft": {
            "zip": "71111",
            "city": "Bossier City",
            "total_charge": 70,
            "price_per_ft": 1.54
          },
          "50ft": {
            "zip": "71111",
            "city": "Bossier City",
            "total_charge": 69,
            "price_per_ft": 1.38
          },
          "55ft": {
            "zip": "71111",
            "city": "Bossier City",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "71111",
            "city": "Bossier City",
            "total_charge": 87,
            "price_per_ft": 1.44
          }
        },
        "summary": {
          "avg_total_charge": 62.92,
          "avg_price_per_ft": 2.56,
          "min_total_charge": 32.0,
          "max_total_charge": 87.0
        }
      }
    }
  },
  "MA": {
    "locations": {
      "South Yarmouth|02664": {
        "city": "South Yarmouth",
        "zip": "02664",
        "entries": {
          "5ft": {
            "zip": "02664",
            "city": "South Yarmouth",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "02664",
            "city": "South Yarmouth",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "02664",
            "city": "South Yarmouth",
            "total_charge": 68,
            "price_per_ft": 4.47
          },
          "20ft": {
            "zip": "02664",
            "city": "South Yarmouth",
            "total_charge": 67,
            "price_per_ft": 3.35
          },
          "25ft": {
            "zip": "02664",
            "city": "South Yarmouth",
            "total_charge": 67,
            "price_per_ft": 2.68
          },
          "30ft": {
            "zip": "02664",
            "city": "South Yarmouth",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "02664",
            "city": "South Yarmouth",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "02664",
            "city": "South Yarmouth",
            "total_charge": 76,
            "price_per_ft": 1.88
          },
          "45ft": {
            "zip": "02664",
            "city": "South Yarmouth",
            "total_charge": 76,
            "price_per_ft": 1.67
          },
          "50ft": {
            "zip": "02664",
            "city": "South Yarmouth",
            "total_charge": 75,
            "price_per_ft": 1.5
          },
          "55ft": {
            "zip": "02664",
            "city": "South Yarmouth",
            "total_charge": 79,
            "price_per_ft": 1.42
          },
          "60ft": {
            "zip": "02664",
            "city": "South Yarmouth",
            "total_charge": 99,
            "price_per_ft": 1.64
          }
        },
        "summary": {
          "avg_total_charge": 66.75,
          "avg_price_per_ft": 2.47,
          "min_total_charge": 17.0,
          "max_total_charge": 99.0
        }
      }
    }
  },
  "MD": {
    "locations": {
      "Havre De Grace|21078": {
        "city": "Havre De Grace",
        "zip": "21078",
        "entries": {
          "5ft": {
            "zip": "21078",
            "city": "Havre De Grace",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "21078",
            "city": "Havre De Grace",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "21078",
            "city": "Havre De Grace",
            "total_charge": 71,
            "price_per_ft": 4.67
          },
          "20ft": {
            "zip": "21078",
            "city": "Havre De Grace",
            "total_charge": 70,
            "price_per_ft": 3.5
          },
          "25ft": {
            "zip": "21078",
            "city": "Havre De Grace",
            "total_charge": 70,
            "price_per_ft": 2.8
          },
          "30ft": {
            "zip": "21078",
            "city": "Havre De Grace",
            "total_charge": 77,
            "price_per_ft": 2.54
          },
          "35ft": {
            "zip": "21078",
            "city": "Havre De Grace",
            "total_charge": 77,
            "price_per_ft": 2.19
          },
          "40ft": {
            "zip": "21078",
            "city": "Havre De Grace",
            "total_charge": 80,
            "price_per_ft": 1.98
          },
          "45ft": {
            "zip": "21078",
            "city": "Havre De Grace",
            "total_charge": 80,
            "price_per_ft": 1.76
          },
          "50ft": {
            "zip": "21078",
            "city": "Havre De Grace",
            "total_charge": 79,
            "price_per_ft": 1.58
          },
          "55ft": {
            "zip": "21078",
            "city": "Havre De Grace",
            "total_charge": 83,
            "price_per_ft": 1.5
          },
          "60ft": {
            "zip": "21078",
            "city": "Havre De Grace",
            "total_charge": 102,
            "price_per_ft": 1.7
          }
        },
        "summary": {
          "avg_total_charge": 69.83,
          "avg_price_per_ft": 2.57,
          "min_total_charge": 17.0,
          "max_total_charge": 102.0
        }
      }
    }
  },
  "ME": {
    "locations": {
      "Belfast|04915": {
        "city": "Belfast",
        "zip": "04915",
        "entries": {
          "5ft": {
            "zip": "04915",
            "city": "Belfast",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "04915",
            "city": "Belfast",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "04915",
            "city": "Belfast",
            "total_charge": 71,
            "price_per_ft": 4.67
          },
          "20ft": {
            "zip": "04915",
            "city": "Belfast",
            "total_charge": 70,
            "price_per_ft": 3.5
          },
          "25ft": {
            "zip": "04915",
            "city": "Belfast",
            "total_charge": 70,
            "price_per_ft": 2.8
          },
          "30ft": {
            "zip": "04915",
            "city": "Belfast",
            "total_charge": 77,
            "price_per_ft": 2.54
          },
          "35ft": {
            "zip": "04915",
            "city": "Belfast",
            "total_charge": 77,
            "price_per_ft": 2.19
          },
          "40ft": {
            "zip": "04915",
            "city": "Belfast",
            "total_charge": 80,
            "price_per_ft": 1.98
          },
          "45ft": {
            "zip": "04915",
            "city": "Belfast",
            "total_charge": 80,
            "price_per_ft": 1.76
          },
          "50ft": {
            "zip": "04915",
            "city": "Belfast",
            "total_charge": 79,
            "price_per_ft": 1.58
          },
          "55ft": {
            "zip": "04915",
            "city": "Belfast",
            "total_charge": 83,
            "price_per_ft": 1.5
          },
          "60ft": {
            "zip": "04915",
            "city": "Belfast",
            "total_charge": 102,
            "price_per_ft": 1.7
          }
        },
        "summary": {
          "avg_total_charge": 69.83,
          "avg_price_per_ft": 2.57,
          "min_total_charge": 17.0,
          "max_total_charge": 102.0
        }
      }
    }
  },
  "MI": {
    "locations": {
      "Baroda|49101": {
        "city": "Baroda",
        "zip": "49101",
        "entries": {
          "5ft": {
            "zip": "49101",
            "city": "Baroda",
            "total_charge": 33,
            "price_per_ft": 6.6
          },
          "10ft": {
            "zip": "49101",
            "city": "Baroda",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "49101",
            "city": "Baroda",
            "total_charge": 66,
            "price_per_ft": 4.34
          },
          "20ft": {
            "zip": "49101",
            "city": "Baroda",
            "total_charge": 65,
            "price_per_ft": 3.25
          },
          "25ft": {
            "zip": "49101",
            "city": "Baroda",
            "total_charge": 65,
            "price_per_ft": 2.6
          },
          "30ft": {
            "zip": "49101",
            "city": "Baroda",
            "total_charge": 72,
            "price_per_ft": 2.37
          },
          "35ft": {
            "zip": "49101",
            "city": "Baroda",
            "total_charge": 72,
            "price_per_ft": 2.03
          },
          "40ft": {
            "zip": "49101",
            "city": "Baroda",
            "total_charge": 74,
            "price_per_ft": 1.83
          },
          "45ft": {
            "zip": "49101",
            "city": "Baroda",
            "total_charge": 74,
            "price_per_ft": 1.63
          },
          "50ft": {
            "zip": "49101",
            "city": "Baroda",
            "total_charge": 73,
            "price_per_ft": 1.46
          },
          "55ft": {
            "zip": "49101",
            "city": "Baroda",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "49101",
            "city": "Baroda",
            "total_charge": 102,
            "price_per_ft": 1.7
          }
        },
        "summary": {
          "avg_total_charge": 66.92,
          "avg_price_per_ft": 2.7,
          "min_total_charge": 32.0,
          "max_total_charge": 102.0
        }
      }
    }
  },
  "MN": {
    "locations": {
      "Deerwood|56444": {
        "city": "Deerwood",
        "zip": "56444",
        "entries": {
          "5ft": {
            "zip": "56444",
            "city": "Deerwood",
            "total_charge": 33,
            "price_per_ft": 6.6
          },
          "10ft": {
            "zip": "56444",
            "city": "Deerwood",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "56444",
            "city": "Deerwood",
            "total_charge": 60,
            "price_per_ft": 4.0
          },
          "20ft": {
            "zip": "56444",
            "city": "Deerwood",
            "total_charge": 60,
            "price_per_ft": 3.0
          },
          "25ft": {
            "zip": "56444",
            "city": "Deerwood",
            "total_charge": 60,
            "price_per_ft": 2.4
          },
          "30ft": {
            "zip": "56444",
            "city": "Deerwood",
            "total_charge": 65,
            "price_per_ft": 2.14
          },
          "35ft": {
            "zip": "56444",
            "city": "Deerwood",
            "total_charge": 65,
            "price_per_ft": 1.83
          },
          "40ft": {
            "zip": "56444",
            "city": "Deerwood",
            "total_charge": 66,
            "price_per_ft": 1.65
          },
          "45ft": {
            "zip": "56444",
            "city": "Deerwood",
            "total_charge": 67,
            "price_per_ft": 1.47
          },
          "50ft": {
            "zip": "56444",
            "city": "Deerwood",
            "total_charge": 68,
            "price_per_ft": 1.35
          },
          "55ft": {
            "zip": "56444",
            "city": "Deerwood",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "56444",
            "city": "Deerwood",
            "total_charge": 84,
            "price_per_ft": 1.39
          }
        },
        "summary": {
          "avg_total_charge": 61.25,
          "avg_price_per_ft": 2.53,
          "min_total_charge": 32.0,
          "max_total_charge": 84.0
        }
      }
    }
  },
  "MO": {
    "locations": {
      "Harrisonville|64701": {
        "city": "Harrisonville",
        "zip": "64701",
        "entries": {
          "5ft": {
            "zip": "64701",
            "city": "Harrisonville",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "64701",
            "city": "Harrisonville",
            "total_charge": 37,
            "price_per_ft": 3.7
          },
          "15ft": {
            "zip": "64701",
            "city": "Harrisonville",
            "total_charge": 66,
            "price_per_ft": 4.34
          },
          "20ft": {
            "zip": "64701",
            "city": "Harrisonville",
            "total_charge": 65,
            "price_per_ft": 3.25
          },
          "25ft": {
            "zip": "64701",
            "city": "Harrisonville",
            "total_charge": 65,
            "price_per_ft": 2.6
          },
          "30ft": {
            "zip": "64701",
            "city": "Harrisonville",
            "total_charge": 72,
            "price_per_ft": 2.37
          },
          "35ft": {
            "zip": "64701",
            "city": "Harrisonville",
            "total_charge": 72,
            "price_per_ft": 2.03
          },
          "40ft": {
            "zip": "64701",
            "city": "Harrisonville",
            "total_charge": 74,
            "price_per_ft": 1.83
          },
          "45ft": {
            "zip": "64701",
            "city": "Harrisonville",
            "total_charge": 74,
            "price_per_ft": 1.63
          },
          "50ft": {
            "zip": "64701",
            "city": "Harrisonville",
            "total_charge": 73,
            "price_per_ft": 1.46
          },
          "55ft": {
            "zip": "64701",
            "city": "Harrisonville",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "64701",
            "city": "Harrisonville",
            "total_charge": 90,
            "price_per_ft": 1.5
          }
        },
        "summary": {
          "avg_total_charge": 65.0,
          "avg_price_per_ft": 2.46,
          "min_total_charge": 17.0,
          "max_total_charge": 90.0
        }
      }
    }
  },
  "MS": {
    "locations": {
      "Greenville|38701": {
        "city": "Greenville",
        "zip": "38701",
        "entries": {
          "5ft": {
            "zip": "38701",
            "city": "Greenville",
            "total_charge": 33,
            "price_per_ft": 6.6
          },
          "10ft": {
            "zip": "38701",
            "city": "Greenville",
            "total_charge": 41,
            "price_per_ft": 4.1
          },
          "15ft": {
            "zip": "38701",
            "city": "Greenville",
            "total_charge": 66,
            "price_per_ft": 4.34
          },
          "20ft": {
            "zip": "38701",
            "city": "Greenville",
            "total_charge": 65,
            "price_per_ft": 3.25
          },
          "25ft": {
            "zip": "38701",
            "city": "Greenville",
            "total_charge": 65,
            "price_per_ft": 2.6
          },
          "30ft": {
            "zip": "38701",
            "city": "Greenville",
            "total_charge": 72,
            "price_per_ft": 2.37
          },
          "35ft": {
            "zip": "38701",
            "city": "Greenville",
            "total_charge": 72,
            "price_per_ft": 2.03
          },
          "40ft": {
            "zip": "38701",
            "city": "Greenville",
            "total_charge": 74,
            "price_per_ft": 1.83
          },
          "45ft": {
            "zip": "38701",
            "city": "Greenville",
            "total_charge": 74,
            "price_per_ft": 1.63
          },
          "50ft": {
            "zip": "38701",
            "city": "Greenville",
            "total_charge": 73,
            "price_per_ft": 1.46
          },
          "55ft": {
            "zip": "38701",
            "city": "Greenville",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "38701",
            "city": "Greenville",
            "total_charge": 102,
            "price_per_ft": 1.7
          }
        },
        "summary": {
          "avg_total_charge": 67.67,
          "avg_price_per_ft": 2.77,
          "min_total_charge": 33.0,
          "max_total_charge": 102.0
        }
      }
    }
  },
  "MT": {
    "locations": {
      "Belgrade|59714": {
        "city": "Belgrade",
        "zip": "59714",
        "entries": {
          "5ft": {
            "zip": "59714",
            "city": "Belgrade",
            "total_charge": 27,
            "price_per_ft": 5.4
          },
          "10ft": {
            "zip": "59714",
            "city": "Belgrade",
            "total_charge": 30,
            "price_per_ft": 3.0
          },
          "15ft": {
            "zip": "59714",
            "city": "Belgrade",
            "total_charge": 54,
            "price_per_ft": 3.54
          },
          "20ft": {
            "zip": "59714",
            "city": "Belgrade",
            "total_charge": 53,
            "price_per_ft": 2.65
          },
          "25ft": {
            "zip": "59714",
            "city": "Belgrade",
            "total_charge": 53,
            "price_per_ft": 2.12
          },
          "30ft": {
            "zip": "59714",
            "city": "Belgrade",
            "total_charge": 57,
            "price_per_ft": 1.87
          },
          "35ft": {
            "zip": "59714",
            "city": "Belgrade",
            "total_charge": 54,
            "price_per_ft": 1.52
          },
          "40ft": {
            "zip": "59714",
            "city": "Belgrade",
            "total_charge": 56,
            "price_per_ft": 1.4
          },
          "45ft": {
            "zip": "59714",
            "city": "Belgrade",
            "total_charge": 61,
            "price_per_ft": 1.35
          },
          "50ft": {
            "zip": "59714",
            "city": "Belgrade",
            "total_charge": 68,
            "price_per_ft": 1.35
          },
          "55ft": {
            "zip": "59714",
            "city": "Belgrade",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "59714",
            "city": "Belgrade",
            "total_charge": 81,
            "price_per_ft": 1.35
          }
        },
        "summary": {
          "avg_total_charge": 55.75,
          "avg_price_per_ft": 2.24,
          "min_total_charge": 27.0,
          "max_total_charge": 81.0
        }
      }
    }
  },
  "NC": {
    "locations": {
      "Asheville|28803": {
        "city": "Asheville",
        "zip": "28803",
        "entries": {
          "5ft": {
            "zip": "28803",
            "city": "Asheville",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "28803",
            "city": "Asheville",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "28803",
            "city": "Asheville",
            "total_charge": 68,
            "price_per_ft": 4.47
          },
          "20ft": {
            "zip": "28803",
            "city": "Asheville",
            "total_charge": 67,
            "price_per_ft": 3.35
          },
          "25ft": {
            "zip": "28803",
            "city": "Asheville",
            "total_charge": 67,
            "price_per_ft": 2.68
          },
          "30ft": {
            "zip": "28803",
            "city": "Asheville",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "28803",
            "city": "Asheville",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "28803",
            "city": "Asheville",
            "total_charge": 76,
            "price_per_ft": 1.88
          },
          "45ft": {
            "zip": "28803",
            "city": "Asheville",
            "total_charge": 76,
            "price_per_ft": 1.67
          },
          "50ft": {
            "zip": "28803",
            "city": "Asheville",
            "total_charge": 75,
            "price_per_ft": 1.5
          },
          "55ft": {
            "zip": "28803",
            "city": "Asheville",
            "total_charge": 79,
            "price_per_ft": 1.42
          },
          "60ft": {
            "zip": "28803",
            "city": "Asheville",
            "total_charge": 99,
            "price_per_ft": 1.64
          }
        },
        "summary": {
          "avg_total_charge": 66.75,
          "avg_price_per_ft": 2.47,
          "min_total_charge": 17.0,
          "max_total_charge": 99.0
        }
      }
    }
  },
  "ND": {
    "locations": {
      "West Fargo|58078": {
        "city": "West Fargo",
        "zip": "58078",
        "entries": {
          "5ft": {
            "zip": "58078",
            "city": "West Fargo",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "58078",
            "city": "West Fargo",
            "total_charge": 36,
            "price_per_ft": 3.6
          },
          "15ft": {
            "zip": "58078",
            "city": "West Fargo",
            "total_charge": 56,
            "price_per_ft": 3.67
          },
          "20ft": {
            "zip": "58078",
            "city": "West Fargo",
            "total_charge": 55,
            "price_per_ft": 2.75
          },
          "25ft": {
            "zip": "58078",
            "city": "West Fargo",
            "total_charge": 56,
            "price_per_ft": 2.21
          },
          "30ft": {
            "zip": "58078",
            "city": "West Fargo",
            "total_charge": 60,
            "price_per_ft": 1.97
          },
          "35ft": {
            "zip": "58078",
            "city": "West Fargo",
            "total_charge": 60,
            "price_per_ft": 1.69
          },
          "40ft": {
            "zip": "58078",
            "city": "West Fargo",
            "total_charge": 62,
            "price_per_ft": 1.53
          },
          "45ft": {
            "zip": "58078",
            "city": "West Fargo",
            "total_charge": 62,
            "price_per_ft": 1.36
          },
          "50ft": {
            "zip": "58078",
            "city": "West Fargo",
            "total_charge": 68,
            "price_per_ft": 1.35
          },
          "55ft": {
            "zip": "58078",
            "city": "West Fargo",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "58078",
            "city": "West Fargo",
            "total_charge": 81,
            "price_per_ft": 1.35
          }
        },
        "summary": {
          "avg_total_charge": 57.33,
          "avg_price_per_ft": 2.19,
          "min_total_charge": 17.0,
          "max_total_charge": 81.0
        }
      }
    }
  },
  "NE": {
    "locations": {
      "Omaha|68164": {
        "city": "Omaha",
        "zip": "68164",
        "entries": {
          "5ft": {
            "zip": "68164",
            "city": "Omaha",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "68164",
            "city": "Omaha",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "68164",
            "city": "Omaha",
            "total_charge": 56,
            "price_per_ft": 3.67
          },
          "20ft": {
            "zip": "68164",
            "city": "Omaha",
            "total_charge": 55,
            "price_per_ft": 2.75
          },
          "25ft": {
            "zip": "68164",
            "city": "Omaha",
            "total_charge": 56,
            "price_per_ft": 2.21
          },
          "30ft": {
            "zip": "68164",
            "city": "Omaha",
            "total_charge": 60,
            "price_per_ft": 1.97
          },
          "35ft": {
            "zip": "68164",
            "city": "Omaha",
            "total_charge": 60,
            "price_per_ft": 1.69
          },
          "40ft": {
            "zip": "68164",
            "city": "Omaha",
            "total_charge": 62,
            "price_per_ft": 1.53
          },
          "45ft": {
            "zip": "68164",
            "city": "Omaha",
            "total_charge": 62,
            "price_per_ft": 1.36
          },
          "50ft": {
            "zip": "68164",
            "city": "Omaha",
            "total_charge": 68,
            "price_per_ft": 1.35
          },
          "55ft": {
            "zip": "68164",
            "city": "Omaha",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "68164",
            "city": "Omaha",
            "total_charge": 81,
            "price_per_ft": 1.35
          }
        },
        "summary": {
          "avg_total_charge": 57.0,
          "avg_price_per_ft": 2.15,
          "min_total_charge": 17.0,
          "max_total_charge": 81.0
        }
      }
    }
  },
  "NH": {
    "locations": {
      "Chester|03036": {
        "city": "Chester",
        "zip": "03036",
        "entries": {
          "5ft": {
            "zip": "03036",
            "city": "Chester",
            "total_charge": 33,
            "price_per_ft": 6.6
          },
          "10ft": {
            "zip": "03036",
            "city": "Chester",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "03036",
            "city": "Chester",
            "total_charge": 72,
            "price_per_ft": 4.8
          },
          "20ft": {
            "zip": "03036",
            "city": "Chester",
            "total_charge": 72,
            "price_per_ft": 3.6
          },
          "25ft": {
            "zip": "03036",
            "city": "Chester",
            "total_charge": 72,
            "price_per_ft": 2.88
          },
          "30ft": {
            "zip": "03036",
            "city": "Chester",
            "total_charge": 78,
            "price_per_ft": 2.57
          },
          "35ft": {
            "zip": "03036",
            "city": "Chester",
            "total_charge": 78,
            "price_per_ft": 2.21
          },
          "40ft": {
            "zip": "03036",
            "city": "Chester",
            "total_charge": 80,
            "price_per_ft": 2.0
          },
          "45ft": {
            "zip": "03036",
            "city": "Chester",
            "total_charge": 81,
            "price_per_ft": 1.78
          },
          "50ft": {
            "zip": "03036",
            "city": "Chester",
            "total_charge": 80,
            "price_per_ft": 1.6
          },
          "55ft": {
            "zip": "03036",
            "city": "Chester",
            "total_charge": 84,
            "price_per_ft": 1.51
          },
          "60ft": {
            "zip": "03036",
            "city": "Chester",
            "total_charge": 102,
            "price_per_ft": 1.7
          }
        },
        "summary": {
          "avg_total_charge": 72.0,
          "avg_price_per_ft": 2.87,
          "min_total_charge": 32.0,
          "max_total_charge": 102.0
        }
      }
    }
  },
  "NJ": {
    "locations": {
      "South Hackensack|07606": {
        "city": "South Hackensack",
        "zip": "07606",
        "entries": {
          "5ft": {
            "zip": "07606",
            "city": "South Hackensack",
            "total_charge": 33,
            "price_per_ft": 6.6
          },
          "10ft": {
            "zip": "07606",
            "city": "South Hackensack",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "07606",
            "city": "South Hackensack",
            "total_charge": 68,
            "price_per_ft": 4.47
          },
          "20ft": {
            "zip": "07606",
            "city": "South Hackensack",
            "total_charge": 67,
            "price_per_ft": 3.35
          },
          "25ft": {
            "zip": "07606",
            "city": "South Hackensack",
            "total_charge": 67,
            "price_per_ft": 2.68
          },
          "30ft": {
            "zip": "07606",
            "city": "South Hackensack",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "07606",
            "city": "South Hackensack",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "07606",
            "city": "South Hackensack",
            "total_charge": 76,
            "price_per_ft": 1.88
          },
          "45ft": {
            "zip": "07606",
            "city": "South Hackensack",
            "total_charge": 76,
            "price_per_ft": 1.67
          },
          "50ft": {
            "zip": "07606",
            "city": "South Hackensack",
            "total_charge": 75,
            "price_per_ft": 1.5
          },
          "55ft": {
            "zip": "07606",
            "city": "South Hackensack",
            "total_charge": 79,
            "price_per_ft": 1.42
          },
          "60ft": {
            "zip": "07606",
            "city": "South Hackensack",
            "total_charge": 99,
            "price_per_ft": 1.64
          }
        },
        "summary": {
          "avg_total_charge": 68.08,
          "avg_price_per_ft": 2.74,
          "min_total_charge": 32.0,
          "max_total_charge": 99.0
        }
      }
    }
  },
  "NM": {
    "locations": {
      "Albuquerque|87113": {
        "city": "Albuquerque",
        "zip": "87113",
        "entries": {
          "5ft": {
            "zip": "87113",
            "city": "Albuquerque",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "87113",
            "city": "Albuquerque",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "87113",
            "city": "Albuquerque",
            "total_charge": 56,
            "price_per_ft": 3.67
          },
          "20ft": {
            "zip": "87113",
            "city": "Albuquerque",
            "total_charge": 55,
            "price_per_ft": 2.75
          },
          "25ft": {
            "zip": "87113",
            "city": "Albuquerque",
            "total_charge": 56,
            "price_per_ft": 2.21
          },
          "30ft": {
            "zip": "87113",
            "city": "Albuquerque",
            "total_charge": 60,
            "price_per_ft": 1.97
          },
          "35ft": {
            "zip": "87113",
            "city": "Albuquerque",
            "total_charge": 60,
            "price_per_ft": 1.69
          },
          "40ft": {
            "zip": "87113",
            "city": "Albuquerque",
            "total_charge": 62,
            "price_per_ft": 1.53
          },
          "45ft": {
            "zip": "87113",
            "city": "Albuquerque",
            "total_charge": 62,
            "price_per_ft": 1.36
          },
          "50ft": {
            "zip": "87113",
            "city": "Albuquerque",
            "total_charge": 68,
            "price_per_ft": 1.35
          },
          "55ft": {
            "zip": "87113",
            "city": "Albuquerque",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "87113",
            "city": "Albuquerque",
            "total_charge": 81,
            "price_per_ft": 1.35
          }
        },
        "summary": {
          "avg_total_charge": 57.0,
          "avg_price_per_ft": 2.15,
          "min_total_charge": 17.0,
          "max_total_charge": 81.0
        }
      }
    }
  },
  "NV": {
    "locations": {
      "Carson City|89703": {
        "city": "Carson City",
        "zip": "89703",
        "entries": {
          "5ft": {
            "zip": "89703",
            "city": "Carson City",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "89703",
            "city": "Carson City",
            "total_charge": 30,
            "price_per_ft": 3.0
          },
          "15ft": {
            "zip": "89703",
            "city": "Carson City",
            "total_charge": 42,
            "price_per_ft": 2.8
          },
          "20ft": {
            "zip": "89703",
            "city": "Carson City",
            "total_charge": 42,
            "price_per_ft": 2.1
          },
          "25ft": {
            "zip": "89703",
            "city": "Carson City",
            "total_charge": 42,
            "price_per_ft": 1.68
          },
          "30ft": {
            "zip": "89703",
            "city": "Carson City",
            "total_charge": 45,
            "price_per_ft": 1.5
          },
          "35ft": {
            "zip": "89703",
            "city": "Carson City",
            "total_charge": 48,
            "price_per_ft": 1.35
          },
          "40ft": {
            "zip": "89703",
            "city": "Carson City",
            "total_charge": 54,
            "price_per_ft": 1.35
          },
          "45ft": {
            "zip": "89703",
            "city": "Carson City",
            "total_charge": 61,
            "price_per_ft": 1.35
          },
          "50ft": {
            "zip": "89703",
            "city": "Carson City",
            "total_charge": 68,
            "price_per_ft": 1.35
          },
          "55ft": {
            "zip": "89703",
            "city": "Carson City",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "89703",
            "city": "Carson City",
            "total_charge": 81,
            "price_per_ft": 1.35
          }
        },
        "summary": {
          "avg_total_charge": 50.42,
          "avg_price_per_ft": 1.88,
          "min_total_charge": 17.0,
          "max_total_charge": 81.0
        }
      }
    }
  },
  "NY": {
    "locations": {
      "Brooklyn|11207-1222": {
        "city": "Brooklyn",
        "zip": "11207-1222",
        "entries": {
          "5ft": {
            "zip": "11207-1222",
            "city": "Brooklyn",
            "total_charge": 33,
            "price_per_ft": 6.6
          },
          "10ft": {
            "zip": "11207-1222",
            "city": "Brooklyn",
            "total_charge": 41,
            "price_per_ft": 4.1
          },
          "15ft": {
            "zip": "11207-1222",
            "city": "Brooklyn",
            "total_charge": 68,
            "price_per_ft": 4.47
          },
          "20ft": {
            "zip": "11207-1222",
            "city": "Brooklyn",
            "total_charge": 67,
            "price_per_ft": 3.35
          },
          "25ft": {
            "zip": "11207-1222",
            "city": "Brooklyn",
            "total_charge": 67,
            "price_per_ft": 2.68
          },
          "30ft": {
            "zip": "11207-1222",
            "city": "Brooklyn",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "11207-1222",
            "city": "Brooklyn",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "11207-1222",
            "city": "Brooklyn",
            "total_charge": 76,
            "price_per_ft": 1.88
          },
          "45ft": {
            "zip": "11207-1222",
            "city": "Brooklyn",
            "total_charge": 76,
            "price_per_ft": 1.67
          },
          "50ft": {
            "zip": "11207-1222",
            "city": "Brooklyn",
            "total_charge": 75,
            "price_per_ft": 1.5
          },
          "55ft": {
            "zip": "11207-1222",
            "city": "Brooklyn",
            "total_charge": 79,
            "price_per_ft": 1.42
          },
          "60ft": {
            "zip": "11207-1222",
            "city": "Brooklyn",
            "total_charge": 99,
            "price_per_ft": 1.64
          }
        },
        "summary": {
          "avg_total_charge": 68.83,
          "avg_price_per_ft": 2.81,
          "min_total_charge": 33.0,
          "max_total_charge": 99.0
        }
      }
    }
  },
  "OH": {
    "locations": {
      "Youngstown|44514": {
        "city": "Youngstown",
        "zip": "44514",
        "entries": {
          "5ft": {
            "zip": "44514",
            "city": "Youngstown",
            "total_charge": 33,
            "price_per_ft": 6.6
          },
          "10ft": {
            "zip": "44514",
            "city": "Youngstown",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "44514",
            "city": "Youngstown",
            "total_charge": 68,
            "price_per_ft": 4.47
          },
          "20ft": {
            "zip": "44514",
            "city": "Youngstown",
            "total_charge": 67,
            "price_per_ft": 3.35
          },
          "25ft": {
            "zip": "44514",
            "city": "Youngstown",
            "total_charge": 67,
            "price_per_ft": 2.68
          },
          "30ft": {
            "zip": "44514",
            "city": "Youngstown",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "44514",
            "city": "Youngstown",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "44514",
            "city": "Youngstown",
            "total_charge": 76,
            "price_per_ft": 1.88
          },
          "45ft": {
            "zip": "44514",
            "city": "Youngstown",
            "total_charge": 76,
            "price_per_ft": 1.67
          },
          "50ft": {
            "zip": "44514",
            "city": "Youngstown",
            "total_charge": 75,
            "price_per_ft": 1.5
          },
          "55ft": {
            "zip": "44514",
            "city": "Youngstown",
            "total_charge": 79,
            "price_per_ft": 1.42
          },
          "60ft": {
            "zip": "44514",
            "city": "Youngstown",
            "total_charge": 99,
            "price_per_ft": 1.64
          }
        },
        "summary": {
          "avg_total_charge": 68.08,
          "avg_price_per_ft": 2.74,
          "min_total_charge": 32.0,
          "max_total_charge": 99.0
        }
      }
    }
  },
  "OK": {
    "locations": {
      "Newalla|74857": {
        "city": "Newalla",
        "zip": "74857",
        "entries": {
          "5ft": {
            "zip": "74857",
            "city": "Newalla",
            "total_charge": 32,
            "price_per_ft": 6.4
          },
          "10ft": {
            "zip": "74857",
            "city": "Newalla",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "74857",
            "city": "Newalla",
            "total_charge": 67,
            "price_per_ft": 4.41
          },
          "20ft": {
            "zip": "74857",
            "city": "Newalla",
            "total_charge": 66,
            "price_per_ft": 3.3
          },
          "25ft": {
            "zip": "74857",
            "city": "Newalla",
            "total_charge": 66,
            "price_per_ft": 2.64
          },
          "30ft": {
            "zip": "74857",
            "city": "Newalla",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "74857",
            "city": "Newalla",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "74857",
            "city": "Newalla",
            "total_charge": 74,
            "price_per_ft": 1.85
          },
          "45ft": {
            "zip": "74857",
            "city": "Newalla",
            "total_charge": 75,
            "price_per_ft": 1.65
          },
          "50ft": {
            "zip": "74857",
            "city": "Newalla",
            "total_charge": 74,
            "price_per_ft": 1.48
          },
          "55ft": {
            "zip": "74857",
            "city": "Newalla",
            "total_charge": 76,
            "price_per_ft": 1.37
          },
          "60ft": {
            "zip": "74857",
            "city": "Newalla",
            "total_charge": 92,
            "price_per_ft": 1.52
          }
        },
        "summary": {
          "avg_total_charge": 66.58,
          "avg_price_per_ft": 2.69,
          "min_total_charge": 32.0,
          "max_total_charge": 92.0
        }
      }
    }
  },
  "OR": {
    "locations": {
      "Milwaukie|97222": {
        "city": "Milwaukie",
        "zip": "97222",
        "entries": {
          "5ft": {
            "zip": "97222",
            "city": "Milwaukie",
            "total_charge": 26,
            "price_per_ft": 5.2
          },
          "10ft": {
            "zip": "97222",
            "city": "Milwaukie",
            "total_charge": 28,
            "price_per_ft": 2.8
          },
          "15ft": {
            "zip": "97222",
            "city": "Milwaukie",
            "total_charge": 36,
            "price_per_ft": 2.34
          },
          "20ft": {
            "zip": "97222",
            "city": "Milwaukie",
            "total_charge": 35,
            "price_per_ft": 1.75
          },
          "25ft": {
            "zip": "97222",
            "city": "Milwaukie",
            "total_charge": 35,
            "price_per_ft": 1.4
          },
          "30ft": {
            "zip": "97222",
            "city": "Milwaukie",
            "total_charge": 41,
            "price_per_ft": 1.35
          },
          "35ft": {
            "zip": "97222",
            "city": "Milwaukie",
            "total_charge": 48,
            "price_per_ft": 1.35
          },
          "40ft": {
            "zip": "97222",
            "city": "Milwaukie",
            "total_charge": 54,
            "price_per_ft": 1.35
          },
          "45ft": {
            "zip": "97222",
            "city": "Milwaukie",
            "total_charge": 61,
            "price_per_ft": 1.35
          },
          "50ft": {
            "zip": "97222",
            "city": "Milwaukie",
            "total_charge": 68,
            "price_per_ft": 1.35
          },
          "55ft": {
            "zip": "97222",
            "city": "Milwaukie",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "97222",
            "city": "Milwaukie",
            "total_charge": 81,
            "price_per_ft": 1.35
          }
        },
        "summary": {
          "avg_total_charge": 49.0,
          "avg_price_per_ft": 1.91,
          "min_total_charge": 26.0,
          "max_total_charge": 81.0
        }
      }
    }
  },
  "PA": {
    "locations": {
      "Kunkletown|18058": {
        "city": "Kunkletown",
        "zip": "18058",
        "entries": {
          "5ft": {
            "zip": "18058",
            "city": "Kunkletown",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "18058",
            "city": "Kunkletown",
            "total_charge": 45,
            "price_per_ft": 4.5
          },
          "15ft": {
            "zip": "18058",
            "city": "Kunkletown",
            "total_charge": 72,
            "price_per_ft": 4.8
          },
          "20ft": {
            "zip": "18058",
            "city": "Kunkletown",
            "total_charge": 72,
            "price_per_ft": 3.6
          },
          "25ft": {
            "zip": "18058",
            "city": "Kunkletown",
            "total_charge": 72,
            "price_per_ft": 2.88
          },
          "30ft": {
            "zip": "18058",
            "city": "Kunkletown",
            "total_charge": 78,
            "price_per_ft": 2.57
          },
          "35ft": {
            "zip": "18058",
            "city": "Kunkletown",
            "total_charge": 78,
            "price_per_ft": 2.21
          },
          "40ft": {
            "zip": "18058",
            "city": "Kunkletown",
            "total_charge": 80,
            "price_per_ft": 2.0
          },
          "45ft": {
            "zip": "18058",
            "city": "Kunkletown",
            "total_charge": 81,
            "price_per_ft": 1.78
          },
          "50ft": {
            "zip": "18058",
            "city": "Kunkletown",
            "total_charge": 80,
            "price_per_ft": 1.6
          },
          "55ft": {
            "zip": "18058",
            "city": "Kunkletown",
            "total_charge": 84,
            "price_per_ft": 1.51
          },
          "60ft": {
            "zip": "18058",
            "city": "Kunkletown",
            "total_charge": 104,
            "price_per_ft": 1.72
          }
        },
        "summary": {
          "avg_total_charge": 71.92,
          "avg_price_per_ft": 2.71,
          "min_total_charge": 17.0,
          "max_total_charge": 104.0
        }
      }
    }
  },
  "RI": {
    "locations": {
      "West Kingston|02892": {
        "city": "West Kingston",
        "zip": "02892",
        "entries": {
          "5ft": {
            "zip": "02892",
            "city": "West Kingston",
            "total_charge": 36,
            "price_per_ft": 7.2
          },
          "10ft": {
            "zip": "02892",
            "city": "West Kingston",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "02892",
            "city": "West Kingston",
            "total_charge": 72,
            "price_per_ft": 4.8
          },
          "20ft": {
            "zip": "02892",
            "city": "West Kingston",
            "total_charge": 72,
            "price_per_ft": 3.6
          },
          "25ft": {
            "zip": "02892",
            "city": "West Kingston",
            "total_charge": 72,
            "price_per_ft": 2.88
          },
          "30ft": {
            "zip": "02892",
            "city": "West Kingston",
            "total_charge": 78,
            "price_per_ft": 2.57
          },
          "35ft": {
            "zip": "02892",
            "city": "West Kingston",
            "total_charge": 78,
            "price_per_ft": 2.21
          },
          "40ft": {
            "zip": "02892",
            "city": "West Kingston",
            "total_charge": 80,
            "price_per_ft": 2.0
          },
          "45ft": {
            "zip": "02892",
            "city": "West Kingston",
            "total_charge": 81,
            "price_per_ft": 1.78
          },
          "50ft": {
            "zip": "02892",
            "city": "West Kingston",
            "total_charge": 80,
            "price_per_ft": 1.6
          },
          "55ft": {
            "zip": "02892",
            "city": "West Kingston",
            "total_charge": 84,
            "price_per_ft": 1.51
          },
          "60ft": {
            "zip": "02892",
            "city": "West Kingston",
            "total_charge": 104,
            "price_per_ft": 1.72
          }
        },
        "summary": {
          "avg_total_charge": 72.42,
          "avg_price_per_ft": 2.92,
          "min_total_charge": 32.0,
          "max_total_charge": 104.0
        }
      }
    }
  },
  "SC": {
    "locations": {
      "Greenville|29615": {
        "city": "Greenville",
        "zip": "29615",
        "entries": {
          "5ft": {
            "zip": "29615",
            "city": "Greenville",
            "total_charge": 33,
            "price_per_ft": 6.6
          },
          "10ft": {
            "zip": "29615",
            "city": "Greenville",
            "total_charge": 41,
            "price_per_ft": 4.1
          },
          "15ft": {
            "zip": "29615",
            "city": "Greenville",
            "total_charge": 68,
            "price_per_ft": 4.47
          },
          "20ft": {
            "zip": "29615",
            "city": "Greenville",
            "total_charge": 67,
            "price_per_ft": 3.35
          },
          "25ft": {
            "zip": "29615",
            "city": "Greenville",
            "total_charge": 67,
            "price_per_ft": 2.68
          },
          "30ft": {
            "zip": "29615",
            "city": "Greenville",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "29615",
            "city": "Greenville",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "29615",
            "city": "Greenville",
            "total_charge": 76,
            "price_per_ft": 1.88
          },
          "45ft": {
            "zip": "29615",
            "city": "Greenville",
            "total_charge": 76,
            "price_per_ft": 1.67
          },
          "50ft": {
            "zip": "29615",
            "city": "Greenville",
            "total_charge": 75,
            "price_per_ft": 1.5
          },
          "55ft": {
            "zip": "29615",
            "city": "Greenville",
            "total_charge": 79,
            "price_per_ft": 1.42
          },
          "60ft": {
            "zip": "29615",
            "city": "Greenville",
            "total_charge": 99,
            "price_per_ft": 1.64
          }
        },
        "summary": {
          "avg_total_charge": 68.83,
          "avg_price_per_ft": 2.81,
          "min_total_charge": 33.0,
          "max_total_charge": 99.0
        }
      }
    }
  },
  "SD": {
    "locations": {
      "Pierre|57501": {
        "city": "Pierre",
        "zip": "57501",
        "entries": {
          "5ft": {
            "zip": "57501",
            "city": "Pierre",
            "total_charge": 31,
            "price_per_ft": 6.2
          },
          "10ft": {
            "zip": "57501",
            "city": "Pierre",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "57501",
            "city": "Pierre",
            "total_charge": 60,
            "price_per_ft": 3.94
          },
          "20ft": {
            "zip": "57501",
            "city": "Pierre",
            "total_charge": 59,
            "price_per_ft": 2.95
          },
          "25ft": {
            "zip": "57501",
            "city": "Pierre",
            "total_charge": 59,
            "price_per_ft": 2.36
          },
          "30ft": {
            "zip": "57501",
            "city": "Pierre",
            "total_charge": 63,
            "price_per_ft": 2.1
          },
          "35ft": {
            "zip": "57501",
            "city": "Pierre",
            "total_charge": 63,
            "price_per_ft": 1.8
          },
          "40ft": {
            "zip": "57501",
            "city": "Pierre",
            "total_charge": 64,
            "price_per_ft": 1.6
          },
          "45ft": {
            "zip": "57501",
            "city": "Pierre",
            "total_charge": 65,
            "price_per_ft": 1.43
          },
          "50ft": {
            "zip": "57501",
            "city": "Pierre",
            "total_charge": 68,
            "price_per_ft": 1.35
          },
          "55ft": {
            "zip": "57501",
            "city": "Pierre",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "57501",
            "city": "Pierre",
            "total_charge": 83,
            "price_per_ft": 1.37
          }
        },
        "summary": {
          "avg_total_charge": 60.17,
          "avg_price_per_ft": 2.47,
          "min_total_charge": 31.0,
          "max_total_charge": 83.0
        }
      }
    }
  },
  "TN": {
    "locations": {
      "Wartburg|37887": {
        "city": "Wartburg",
        "zip": "37887",
        "entries": {
          "5ft": {
            "zip": "37887",
            "city": "Wartburg",
            "total_charge": 36,
            "price_per_ft": 7.2
          },
          "10ft": {
            "zip": "37887",
            "city": "Wartburg",
            "total_charge": 45,
            "price_per_ft": 4.5
          },
          "15ft": {
            "zip": "37887",
            "city": "Wartburg",
            "total_charge": 72,
            "price_per_ft": 4.8
          },
          "20ft": {
            "zip": "37887",
            "city": "Wartburg",
            "total_charge": 72,
            "price_per_ft": 3.6
          },
          "25ft": {
            "zip": "37887",
            "city": "Wartburg",
            "total_charge": 72,
            "price_per_ft": 2.88
          },
          "30ft": {
            "zip": "37887",
            "city": "Wartburg",
            "total_charge": 78,
            "price_per_ft": 2.57
          },
          "35ft": {
            "zip": "37887",
            "city": "Wartburg",
            "total_charge": 78,
            "price_per_ft": 2.21
          },
          "40ft": {
            "zip": "37887",
            "city": "Wartburg",
            "total_charge": 80,
            "price_per_ft": 2.0
          },
          "45ft": {
            "zip": "37887",
            "city": "Wartburg",
            "total_charge": 81,
            "price_per_ft": 1.78
          },
          "50ft": {
            "zip": "37887",
            "city": "Wartburg",
            "total_charge": 80,
            "price_per_ft": 1.6
          },
          "55ft": {
            "zip": "37887",
            "city": "Wartburg",
            "total_charge": 84,
            "price_per_ft": 1.51
          },
          "60ft": {
            "zip": "37887",
            "city": "Wartburg",
            "total_charge": 104,
            "price_per_ft": 1.72
          }
        },
        "summary": {
          "avg_total_charge": 73.5,
          "avg_price_per_ft": 3.03,
          "min_total_charge": 36.0,
          "max_total_charge": 104.0
        }
      }
    }
  },
  "TX": {
    "locations": {
      "Spring|77386": {
        "city": "Spring",
        "zip": "77386",
        "entries": {
          "5ft": {
            "zip": "77386",
            "city": "Spring",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "77386",
            "city": "Spring",
            "total_charge": 41,
            "price_per_ft": 4.1
          },
          "15ft": {
            "zip": "77386",
            "city": "Spring",
            "total_charge": 68,
            "price_per_ft": 4.47
          },
          "20ft": {
            "zip": "77386",
            "city": "Spring",
            "total_charge": 67,
            "price_per_ft": 3.35
          },
          "25ft": {
            "zip": "77386",
            "city": "Spring",
            "total_charge": 67,
            "price_per_ft": 2.68
          },
          "30ft": {
            "zip": "77386",
            "city": "Spring",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "77386",
            "city": "Spring",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "77386",
            "city": "Spring",
            "total_charge": 76,
            "price_per_ft": 1.88
          },
          "45ft": {
            "zip": "77386",
            "city": "Spring",
            "total_charge": 76,
            "price_per_ft": 1.67
          },
          "50ft": {
            "zip": "77386",
            "city": "Spring",
            "total_charge": 75,
            "price_per_ft": 1.5
          },
          "55ft": {
            "zip": "77386",
            "city": "Spring",
            "total_charge": 79,
            "price_per_ft": 1.42
          },
          "60ft": {
            "zip": "77386",
            "city": "Spring",
            "total_charge": 99,
            "price_per_ft": 1.64
          }
        },
        "summary": {
          "avg_total_charge": 67.5,
          "avg_price_per_ft": 2.55,
          "min_total_charge": 17.0,
          "max_total_charge": 99.0
        }
      }
    }
  },
  "UT": {
    "locations": {
      "Big Water|84741": {
        "city": "Big Water",
        "zip": "84741",
        "entries": {
          "5ft": {
            "zip": "84741",
            "city": "Big Water",
            "total_charge": 31,
            "price_per_ft": 6.2
          },
          "10ft": {
            "zip": "84741",
            "city": "Big Water",
            "total_charge": 36,
            "price_per_ft": 3.6
          },
          "15ft": {
            "zip": "84741",
            "city": "Big Water",
            "total_charge": 69,
            "price_per_ft": 4.54
          },
          "20ft": {
            "zip": "84741",
            "city": "Big Water",
            "total_charge": 68,
            "price_per_ft": 3.4
          },
          "25ft": {
            "zip": "84741",
            "city": "Big Water",
            "total_charge": 68,
            "price_per_ft": 2.72
          },
          "30ft": {
            "zip": "84741",
            "city": "Big Water",
            "total_charge": 72,
            "price_per_ft": 2.37
          },
          "35ft": {
            "zip": "84741",
            "city": "Big Water",
            "total_charge": 72,
            "price_per_ft": 2.03
          },
          "40ft": {
            "zip": "84741",
            "city": "Big Water",
            "total_charge": 74,
            "price_per_ft": 1.83
          },
          "45ft": {
            "zip": "84741",
            "city": "Big Water",
            "total_charge": 74,
            "price_per_ft": 1.63
          },
          "50ft": {
            "zip": "84741",
            "city": "Big Water",
            "total_charge": 73,
            "price_per_ft": 1.46
          },
          "55ft": {
            "zip": "84741",
            "city": "Big Water",
            "total_charge": 76,
            "price_per_ft": 1.37
          },
          "60ft": {
            "zip": "84741",
            "city": "Big Water",
            "total_charge": 92,
            "price_per_ft": 1.52
          }
        },
        "summary": {
          "avg_total_charge": 67.08,
          "avg_price_per_ft": 2.72,
          "min_total_charge": 31.0,
          "max_total_charge": 92.0
        }
      }
    }
  },
  "VA": {
    "locations": {
      "Quinby|23423": {
        "city": "Quinby",
        "zip": "23423",
        "entries": {
          "5ft": {
            "zip": "23423",
            "city": "Quinby",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "23423",
            "city": "Quinby",
            "total_charge": 45,
            "price_per_ft": 4.5
          },
          "15ft": {
            "zip": "23423",
            "city": "Quinby",
            "total_charge": 72,
            "price_per_ft": 4.8
          },
          "20ft": {
            "zip": "23423",
            "city": "Quinby",
            "total_charge": 72,
            "price_per_ft": 3.6
          },
          "25ft": {
            "zip": "23423",
            "city": "Quinby",
            "total_charge": 72,
            "price_per_ft": 2.88
          },
          "30ft": {
            "zip": "23423",
            "city": "Quinby",
            "total_charge": 78,
            "price_per_ft": 2.57
          },
          "35ft": {
            "zip": "23423",
            "city": "Quinby",
            "total_charge": 78,
            "price_per_ft": 2.21
          },
          "40ft": {
            "zip": "23423",
            "city": "Quinby",
            "total_charge": 80,
            "price_per_ft": 2.0
          },
          "45ft": {
            "zip": "23423",
            "city": "Quinby",
            "total_charge": 81,
            "price_per_ft": 1.78
          },
          "50ft": {
            "zip": "23423",
            "city": "Quinby",
            "total_charge": 80,
            "price_per_ft": 1.6
          },
          "55ft": {
            "zip": "23423",
            "city": "Quinby",
            "total_charge": 84,
            "price_per_ft": 1.51
          },
          "60ft": {
            "zip": "23423",
            "city": "Quinby",
            "total_charge": 104,
            "price_per_ft": 1.72
          }
        },
        "summary": {
          "avg_total_charge": 71.92,
          "avg_price_per_ft": 2.71,
          "min_total_charge": 17.0,
          "max_total_charge": 104.0
        }
      }
    }
  },
  "VT": {
    "locations": {
      "South Burlington|05403": {
        "city": "South Burlington",
        "zip": "05403",
        "entries": {
          "5ft": {
            "zip": "05403",
            "city": "South Burlington",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "05403",
            "city": "South Burlington",
            "total_charge": 41,
            "price_per_ft": 4.1
          },
          "15ft": {
            "zip": "05403",
            "city": "South Burlington",
            "total_charge": 68,
            "price_per_ft": 4.47
          },
          "20ft": {
            "zip": "05403",
            "city": "South Burlington",
            "total_charge": 67,
            "price_per_ft": 3.35
          },
          "25ft": {
            "zip": "05403",
            "city": "South Burlington",
            "total_charge": 67,
            "price_per_ft": 2.68
          },
          "30ft": {
            "zip": "05403",
            "city": "South Burlington",
            "total_charge": 72,
            "price_per_ft": 2.4
          },
          "35ft": {
            "zip": "05403",
            "city": "South Burlington",
            "total_charge": 73,
            "price_per_ft": 2.06
          },
          "40ft": {
            "zip": "05403",
            "city": "South Burlington",
            "total_charge": 76,
            "price_per_ft": 1.88
          },
          "45ft": {
            "zip": "05403",
            "city": "South Burlington",
            "total_charge": 76,
            "price_per_ft": 1.67
          },
          "50ft": {
            "zip": "05403",
            "city": "South Burlington",
            "total_charge": 75,
            "price_per_ft": 1.5
          },
          "55ft": {
            "zip": "05403",
            "city": "South Burlington",
            "total_charge": 79,
            "price_per_ft": 1.42
          },
          "60ft": {
            "zip": "05403",
            "city": "South Burlington",
            "total_charge": 99,
            "price_per_ft": 1.64
          }
        },
        "summary": {
          "avg_total_charge": 67.5,
          "avg_price_per_ft": 2.55,
          "min_total_charge": 17.0,
          "max_total_charge": 99.0
        }
      }
    }
  },
  "WA": {
    "locations": {
      "Federal Way|98003": {
        "city": "Federal Way",
        "zip": "98003",
        "entries": {
          "5ft": {
            "zip": "98003",
            "city": "Federal Way",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "98003",
            "city": "Federal Way",
            "total_charge": 28,
            "price_per_ft": 2.8
          },
          "15ft": {
            "zip": "98003",
            "city": "Federal Way",
            "total_charge": 36,
            "price_per_ft": 2.34
          },
          "20ft": {
            "zip": "98003",
            "city": "Federal Way",
            "total_charge": 35,
            "price_per_ft": 1.75
          },
          "25ft": {
            "zip": "98003",
            "city": "Federal Way",
            "total_charge": 35,
            "price_per_ft": 1.4
          },
          "30ft": {
            "zip": "98003",
            "city": "Federal Way",
            "total_charge": 41,
            "price_per_ft": 1.35
          },
          "35ft": {
            "zip": "98003",
            "city": "Federal Way",
            "total_charge": 48,
            "price_per_ft": 1.35
          },
          "40ft": {
            "zip": "98003",
            "city": "Federal Way",
            "total_charge": 54,
            "price_per_ft": 1.35
          },
          "45ft": {
            "zip": "98003",
            "city": "Federal Way",
            "total_charge": 61,
            "price_per_ft": 1.35
          },
          "50ft": {
            "zip": "98003",
            "city": "Federal Way",
            "total_charge": 68,
            "price_per_ft": 1.35
          },
          "55ft": {
            "zip": "98003",
            "city": "Federal Way",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "98003",
            "city": "Federal Way",
            "total_charge": 81,
            "price_per_ft": 1.35
          }
        },
        "summary": {
          "avg_total_charge": 48.25,
          "avg_price_per_ft": 1.76,
          "min_total_charge": 17.0,
          "max_total_charge": 81.0
        }
      }
    }
  },
  "WI": {
    "locations": {
      "Hudson|54016": {
        "city": "Hudson",
        "zip": "54016",
        "entries": {
          "5ft": {
            "zip": "54016",
            "city": "Hudson",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "54016",
            "city": "Hudson",
            "total_charge": 37,
            "price_per_ft": 3.7
          },
          "15ft": {
            "zip": "54016",
            "city": "Hudson",
            "total_charge": 62,
            "price_per_ft": 4.07
          },
          "20ft": {
            "zip": "54016",
            "city": "Hudson",
            "total_charge": 61,
            "price_per_ft": 3.05
          },
          "25ft": {
            "zip": "54016",
            "city": "Hudson",
            "total_charge": 61,
            "price_per_ft": 2.44
          },
          "30ft": {
            "zip": "54016",
            "city": "Hudson",
            "total_charge": 68,
            "price_per_ft": 2.25
          },
          "35ft": {
            "zip": "54016",
            "city": "Hudson",
            "total_charge": 68,
            "price_per_ft": 1.92
          },
          "40ft": {
            "zip": "54016",
            "city": "Hudson",
            "total_charge": 70,
            "price_per_ft": 1.73
          },
          "45ft": {
            "zip": "54016",
            "city": "Hudson",
            "total_charge": 70,
            "price_per_ft": 1.54
          },
          "50ft": {
            "zip": "54016",
            "city": "Hudson",
            "total_charge": 69,
            "price_per_ft": 1.38
          },
          "55ft": {
            "zip": "54016",
            "city": "Hudson",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "54016",
            "city": "Hudson",
            "total_charge": 87,
            "price_per_ft": 1.45
          }
        },
        "summary": {
          "avg_total_charge": 62.08,
          "avg_price_per_ft": 2.36,
          "min_total_charge": 17.0,
          "max_total_charge": 87.0
        }
      }
    }
  },
  "WV": {
    "locations": {
      "Fayetteville|25840": {
        "city": "Fayetteville",
        "zip": "25840",
        "entries": {
          "5ft": {
            "zip": "25840",
            "city": "Fayetteville",
            "total_charge": 33,
            "price_per_ft": 6.6
          },
          "10ft": {
            "zip": "25840",
            "city": "Fayetteville",
            "total_charge": 32,
            "price_per_ft": 3.2
          },
          "15ft": {
            "zip": "25840",
            "city": "Fayetteville",
            "total_charge": 71,
            "price_per_ft": 4.67
          },
          "20ft": {
            "zip": "25840",
            "city": "Fayetteville",
            "total_charge": 70,
            "price_per_ft": 3.5
          },
          "25ft": {
            "zip": "25840",
            "city": "Fayetteville",
            "total_charge": 70,
            "price_per_ft": 2.8
          },
          "30ft": {
            "zip": "25840",
            "city": "Fayetteville",
            "total_charge": 77,
            "price_per_ft": 2.54
          },
          "35ft": {
            "zip": "25840",
            "city": "Fayetteville",
            "total_charge": 77,
            "price_per_ft": 2.19
          },
          "40ft": {
            "zip": "25840",
            "city": "Fayetteville",
            "total_charge": 80,
            "price_per_ft": 1.98
          },
          "45ft": {
            "zip": "25840",
            "city": "Fayetteville",
            "total_charge": 80,
            "price_per_ft": 1.76
          },
          "50ft": {
            "zip": "25840",
            "city": "Fayetteville",
            "total_charge": 79,
            "price_per_ft": 1.58
          },
          "55ft": {
            "zip": "25840",
            "city": "Fayetteville",
            "total_charge": 83,
            "price_per_ft": 1.5
          },
          "60ft": {
            "zip": "25840",
            "city": "Fayetteville",
            "total_charge": 102,
            "price_per_ft": 1.7
          }
        },
        "summary": {
          "avg_total_charge": 71.17,
          "avg_price_per_ft": 2.83,
          "min_total_charge": 32.0,
          "max_total_charge": 102.0
        }
      }
    }
  },
  "WY": {
    "locations": {
      "Jackson|83001": {
        "city": "Jackson",
        "zip": "83001",
        "entries": {
          "5ft": {
            "zip": "83001",
            "city": "Jackson",
            "total_charge": 17,
            "price_per_ft": 3.4
          },
          "10ft": {
            "zip": "83001",
            "city": "Jackson",
            "total_charge": 33,
            "price_per_ft": 3.3
          },
          "15ft": {
            "zip": "83001",
            "city": "Jackson",
            "total_charge": 54,
            "price_per_ft": 3.54
          },
          "20ft": {
            "zip": "83001",
            "city": "Jackson",
            "total_charge": 53,
            "price_per_ft": 2.65
          },
          "25ft": {
            "zip": "83001",
            "city": "Jackson",
            "total_charge": 53,
            "price_per_ft": 2.12
          },
          "30ft": {
            "zip": "83001",
            "city": "Jackson",
            "total_charge": 57,
            "price_per_ft": 1.87
          },
          "35ft": {
            "zip": "83001",
            "city": "Jackson",
            "total_charge": 56,
            "price_per_ft": 1.6
          },
          "40ft": {
            "zip": "83001",
            "city": "Jackson",
            "total_charge": 58,
            "price_per_ft": 1.45
          },
          "45ft": {
            "zip": "83001",
            "city": "Jackson",
            "total_charge": 61,
            "price_per_ft": 1.35
          },
          "50ft": {
            "zip": "83001",
            "city": "Jackson",
            "total_charge": 68,
            "price_per_ft": 1.35
          },
          "55ft": {
            "zip": "83001",
            "city": "Jackson",
            "total_charge": 75,
            "price_per_ft": 1.35
          },
          "60ft": {
            "zip": "83001",
            "city": "Jackson",
            "total_charge": 81,
            "price_per_ft": 1.35
          }
        },
        "summary": {
          "avg_total_charge": 55.5,
          "avg_price_per_ft": 2.11,
          "min_total_charge": 17.0,
          "max_total_charge": 81.0
        }
      }
    }
  }
};

        d3.json("https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json").then(us => {
            const states = topojson.feature(us, us.objects.states).features;

            svg.append("g")
                .selectAll("path")
                .data(states)
                .join("path")
                .attr("class", "state")
                .attr("d", path)
                .attr("data-name", d => {
                    const id = String(d.id).padStart(2, "0");
                    return stateNames[id] || `State ${id}`;
                })
                .on("mouseenter", function () {
                    d3.select(this).classed("active", true);
                })
                .on("mouseleave", function () {
                    d3.select(this).classed("active", false);
                })
                .on("click", function (event, d) {
                    const id = String(d.id).padStart(2, "0");
                    const stateName = stateNames[id] || `State ${id}`;
                    const stateCode = stateAbbrByFips[id];
                    renderStateModal(stateCode, stateName, shippingData);
                });

            svg.append("path")
                .datum(topojson.mesh(us, us.objects.states, (a, b) => a !== b))
                .attr("class", "state-borders")
                .attr("d", path);

            const dcFeature = states.find(d => String(d.id).padStart(2, "0") === "11");
            if (dcFeature) {
                const [x, y] = path.centroid(dcFeature);
                svg.append("circle")
                    .attr("cx", x)
                    .attr("cy", y)
                    .attr("r", 8)
                    .attr("fill", "rgba(13, 110, 253, 0.55)")
                    .attr("stroke", "#fff")
                    .attr("stroke-width", 1.5)
                    .style("cursor", "pointer")
                    .on("mouseenter", function () {
                        d3.select(this).attr("fill", "rgba(13, 110, 253, 0.9)");
                    })
                    .on("mouseleave", function () {
                        d3.select(this).attr("fill", "rgba(13, 110, 253, 0.55)");
                    })
                    .on("click", function () {
                        renderStateModal("DC", "District of Columbia", shippingData);
                    });
            }
        }).catch(error => {
            console.error(error);
            modalTitle.textContent = "Error";
            modalBody.innerHTML = '<div class="alert alert-danger mb-0">Failed to load map or shipping data.</div>';
            modalEl.open();
        });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
